// src/pages/FieldDetail.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import AppShell from "../components/AppShell.jsx";
import MapboxFieldView from "../components/MapboxFieldView.jsx";
import MapboxFieldEditor from "../components/MapboxFieldEditor.jsx";
import FieldEventPanel from "../components/FieldEventPanel.jsx";
import FieldHistoryPanel from "../components/FieldHistoryPanel.jsx";
import ClimateZonePanel from "../components/ClimateZonePanel.jsx";
import { apiFetch, getStoredUser, safeReadError } from "../api/api.js";

/** -------- helpers -------- */
function pad2(n) {
  return String(n).padStart(2, "0");
}
function fmtDate(s) {
  if (!s) return "—";
  const str = String(s);
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    const [y, m, d] = str.split("-");
    return `${d}.${m}.${y}`;
  }
  const d = new Date(str);
  if (Number.isFinite(d.getTime())) {
    return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`;
  }
  return str;
}
function fmtDateTime(s) {
  if (!s) return "—";
  const d = new Date(String(s));
  if (!Number.isFinite(d.getTime())) return String(s);
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}, ${pad2(
    d.getHours()
  )}:${pad2(d.getMinutes())}`;
}

function clampStr(x, max = 120) {
  const s = String(x ?? "").trim();
  return s.length > max ? s.slice(0, max) : s;
}
function nf(x, digits = 2) {
  const n = Number(x);
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(digits);
}
function emptyDash(v) {
  const s = String(v ?? "").trim();
  return s ? s : "—";
}
function sameStr(a, b) {
  return String(a ?? "") === String(b ?? "");
}

function parseMaybeJson(value) {
  if (!value) return null;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function normalizeFieldFromApi(raw) {
  if (!raw || typeof raw !== "object") return null;

  const geometry =
    raw.geometry ??
    parseMaybeJson(raw.geometryJson) ??
    parseMaybeJson(raw.contour) ??
    parseMaybeJson(raw.shape) ??
    null;

  const fieldGroupId =
    raw.fieldGroupId ??
    raw.groupId ??
    raw.fieldGroup?.id ??
    raw.group?.id ??
    null;

  return {
    ...raw,
    geometry,
    fieldGroupId,
    areaHa:
      raw.areaHa ??
      raw.area ??
      raw.totalArea ??
      raw.hectares ??
      null,
  };
}

function getGeometryOnly(value) {
  const g = parseMaybeJson(value);
  if (!g) return null;
  if (g.type === "Feature" && g.geometry) return g.geometry;
  return g;
}

function computeBBoxFromGeometry(geometry) {
  const g = getGeometryOnly(geometry);
  if (!g) return null;

  let coords = [];

  if (g.type === "Polygon") {
    coords = g.coordinates?.flat(1) || [];
  } else if (g.type === "MultiPolygon") {
    coords = g.coordinates?.flat(2) || [];
  } else {
    return null;
  }

  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;

  for (const p of coords) {
    const lng = Number(p?.[0]);
    const lat = Number(p?.[1]);
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) continue;

    minLng = Math.min(minLng, lng);
    minLat = Math.min(minLat, lat);
    maxLng = Math.max(maxLng, lng);
    maxLat = Math.max(maxLat, lat);
  }

  if (
    !Number.isFinite(minLng) ||
    !Number.isFinite(minLat) ||
    !Number.isFinite(maxLng) ||
    !Number.isFinite(maxLat)
  ) {
    return null;
  }

  return [minLng, minLat, maxLng, maxLat];
}

/** =========================
    ✅ CROPS WHITELIST (UPDATED)
   ========================= */
const ALLOWED_CROP_CATEGORIES = new Set([
  "GRAINS",
  "OILSEEDS",
  "LEGUMES",
  "VEGETABLES",
  "FRUITS",
  "INDUSTRIAL",
  "FORAGE",
  "OTHER",
]);

function isAllowedCrop(c) {
  if (!c || typeof c !== "object") return false;
  const cat = String(c.category ?? "").trim().toUpperCase();
  return ALLOWED_CROP_CATEGORIES.has(cat);
}

/** -------- SeasonField UI dictionary -------- */
const SEASON_FIELD_OPTIONS = [
  { value: "PLANNED",          label: "Заплановано",        icon: "🗓", color: "#94a3b8" },
  { value: "PREPARATION",      label: "Підготовка",         icon: "⚙️", color: "#fb923c" },
  { value: "PLANTED",          label: "Посіяно / Посаджено",icon: "🌱", color: "#4ade80" },
  { value: "EMERGED",          label: "Сходи",              icon: "🌿", color: "#34d399" },
  { value: "GROWING",          label: "Вегетація",          icon: "🌾", color: "#22c55e" },
  { value: "FLOWERING",        label: "Цвітіння",           icon: "🌸", color: "#e879f9" },
  { value: "MATURING",         label: "Дозрівання",         icon: "🍂", color: "#fbbf24" },
  { value: "READY_TO_HARVEST", label: "Готове до збору",    icon: "✅", color: "#86efac" },
  { value: "HARVESTED",        label: "Зібрано",            icon: "🚜", color: "#60a5fa" },
  { value: "COMPLETED",        label: "Завершено",          icon: "🏁", color: "#a3a3a3" },
  { value: "FALLOW", label: "Пар / Без культури" },
];

function seasonFieldLabel(enumVal) {
  const v = String(enumVal || "");
  const found = SEASON_FIELD_OPTIONS.find((x) => x.value === v);
  return found ? found.label : v || "—";
}

function statusTone(v) {
  const s = String(v || "");
  if (s === "COMPLETED") return "done";
  if (s === "HARVESTED" || s === "READY_TO_HARVEST") return "warn";
  if (s === "FLOWERING" || s === "MATURING") return "info";
  if (s === "GROWING" || s === "EMERGED") return "ok";
  if (s === "PLANTED" || s === "PREPARATION") return "ok2";
  if (s === "FALLOW" || s === "PLANNED") return "muted";
  return "muted";
}

function pickSeasonKey(season) {
  if (season?.id != null) return { kind: "id", key: String(season.id) };
  if (season?.year != null) return { kind: "year", key: String(season.year) };
  return { kind: "unknown", key: "" };
}

/** -------- UI helpers -------- */
function KV({ k, v }) {
  return (
    <div className="fd-kv">
      <div className="fd-kvK">{k}</div>
      <div className="fd-kvV">{v}</div>
    </div>
  );
}
function SectionTitle({ title, sub, right }) {
  return (
    <div className="fd-sectionHead">
      <div>
        <div className="fd-sectionTitle">{title}</div>
        {sub ? <div className="fd-sectionSub">{sub}</div> : null}
      </div>
      {right ? <div className="fd-sectionRight">{right}</div> : null}
    </div>
  );
}

/** ===== StatusSelect ===== */
function getScrollParents(node) {
  const parents = [];
  if (!node) return parents;
  let el = node.parentElement;
  while (el) {
    const st = window.getComputedStyle(el);
    const overflow = `${st.overflow}${st.overflowX}${st.overflowY}`;
    if (/(auto|scroll|overlay)/.test(overflow)) parents.push(el);
    el = el.parentElement;
  }
  parents.push(window);
  return parents;
}

function StatusSelect({
  value,
  onChange,
  disabled,
  tone = "muted",
  menuMaxWidth = 560,
  menuMaxHeight = 360,
}) {
  const wrapRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [hoverIdx, setHoverIdx] = useState(-1);

  const [menuStyle, setMenuStyle] = useState({
    position: "fixed",
    left: -9999,
    top: -9999,
    width: 320,
    maxHeight: menuMaxHeight,
    visibility: "hidden",
    pointerEvents: "none",
  });

  const val = String(value || "PLANNED");
  const current =
    SEASON_FIELD_OPTIONS.find((o) => o.value === val) || SEASON_FIELD_OPTIONS[0];

  function close() {
    setOpen(false);
    setHoverIdx(-1);
    setMenuStyle((s) => ({
      ...s,
      left: -9999,
      top: -9999,
      visibility: "hidden",
      pointerEvents: "none",
    }));
  }

  function clamp(n, a, b) {
    return Math.max(a, Math.min(b, n));
  }

  function computeMenu() {
    const wrap = wrapRef.current;
    if (!wrap) return;

    const r = wrap.getBoundingClientRect();
    const vw = window.innerWidth || 1200;
    const vh = window.innerHeight || 800;
    const gap = 10;

    const w = Math.min(menuMaxWidth, Math.max(300, Math.floor(r.width)));
    const spaceBelow = vh - r.bottom;
    const spaceAbove = r.top;
    const openUp = spaceBelow < 240 && spaceAbove > spaceBelow;

    const maxH = Math.min(
      menuMaxHeight,
      Math.max(200, Math.floor(Math.max(spaceBelow, spaceAbove) - 16))
    );
    const left = clamp(Math.floor(r.left), 8, vw - w - 8);

    let top;
    if (!openUp) top = clamp(Math.floor(r.bottom + gap), 8, vh - maxH - 8);
    else top = clamp(Math.floor(r.top - gap - maxH), 8, vh - maxH - 8);

    setMenuStyle({
      position: "fixed",
      left,
      top,
      width: w,
      maxHeight: maxH,
      visibility: "visible",
      pointerEvents: "auto",
    });
  }

  function toggle() {
    if (disabled) return;
    if (open) return close();

    setMenuStyle((s) => ({
      ...s,
      left: -9999,
      top: -9999,
      width: 320,
      maxHeight: menuMaxHeight,
      visibility: "hidden",
      pointerEvents: "none",
    }));
    setOpen(true);
  }

  useEffect(() => {
    function onDocDown(e) {
      const el = wrapRef.current;
      const menu = menuRef.current;
      if (!el) return;
      const t = e.target;
      const insideButton = el.contains(t);
      const insideMenu = menu ? menu.contains(t) : false;
      if (!insideButton && !insideMenu) close();
    }
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, []);

  useEffect(() => {
    function onKey(e) {
      if (!open) return;
      if (e.key === "Escape") {
        e.preventDefault();
        close();
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHoverIdx((i) =>
          Math.min(i < 0 ? 0 : i + 1, SEASON_FIELD_OPTIONS.length - 1)
        );
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHoverIdx((i) =>
          Math.max(i < 0 ? SEASON_FIELD_OPTIONS.length - 1 : i - 1, 0)
        );
        return;
      }
      if (e.key === "Enter") {
        if (hoverIdx >= 0 && hoverIdx < SEASON_FIELD_OPTIONS.length) {
          e.preventDefault();
          const picked = SEASON_FIELD_OPTIONS[hoverIdx];
          onChange?.(picked.value);
          close();
        }
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, hoverIdx, onChange]);

  useEffect(() => {
    if (!open) return;

    const idx = SEASON_FIELD_OPTIONS.findIndex((o) => o.value === val);
    setHoverIdx(idx >= 0 ? idx : 0);

    const raf1 = requestAnimationFrame(() => {
      computeMenu();
      requestAnimationFrame(() => {
        const list = menuRef.current;
        if (!list) return;
        const el = list.querySelector(`[data-idx="${idx}"]`);
        if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
      });
    });

    return () => cancelAnimationFrame(raf1);
  }, [open, val]);

  useEffect(() => {
    if (!open) return;

    const wrap = wrapRef.current;
    const parents = getScrollParents(wrap);
    const onAnyMove = () => computeMenu();

    parents.forEach((p) => {
      if (p === window)
        window.addEventListener("scroll", onAnyMove, {
          passive: true,
          capture: true,
        });
      else p.addEventListener("scroll", onAnyMove, { passive: true });
    });
    window.addEventListener("resize", onAnyMove, { passive: true });

    let ro = null;
    if (wrap && "ResizeObserver" in window) {
      ro = new ResizeObserver(() => onAnyMove());
      ro.observe(wrap);
    }

    return () => {
      parents.forEach((p) => {
        if (p === window)
          window.removeEventListener("scroll", onAnyMove, { capture: true });
        else p.removeEventListener("scroll", onAnyMove);
      });
      window.removeEventListener("resize", onAnyMove);
      if (ro) ro.disconnect();
    };
  }, [open]);

  const menuNode =
    open &&
    createPortal(
      <div
        className="fd-statusDDMenu"
        role="listbox"
        ref={menuRef}
        style={menuStyle}
        tabIndex={-1}
        onWheel={(e) => e.stopPropagation()}
      >
        {SEASON_FIELD_OPTIONS.map((o, idx) => {
          const active = o.value === val;
          const hover = idx === hoverIdx;
          return (
            <button
              type="button"
              key={o.value}
              role="option"
              aria-selected={active ? "true" : "false"}
              className={`fd-statusDDItem ${active ? "isActive" : ""} ${hover ? "isHover" : ""}`}
              onMouseEnter={() => setHoverIdx(idx)}
              onClick={() => { onChange?.(o.value); close(); }}
              data-idx={idx}
            >
              <span className="fd-statusDDItemLeft">
                <span className="fd-statusDDDot" style={{ background: o.color }} />
                <span className="fd-statusDDIcon">{o.icon}</span>
                <span className="fd-statusDDItemText">{o.label}</span>
              </span>
              <span className={`fd-statusDDTick ${active ? "" : "ghost"}`}>✓</span>
            </button>
          );
        })}
      </div>,
      document.body
    );

  return (
    <>
      <div
        ref={wrapRef}
        className="fd-statusDD"
        data-tone={tone}
        aria-disabled={disabled ? "true" : "false"}
      >
        <button
          type="button"
          className="fd-statusDDBtn"
          onClick={toggle}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open ? "true" : "false"}
          title="Статус сезону"
        >
          <span className="fd-statusDDLabel">{current.label}</span>
          <span className="fd-statusDDChevron" aria-hidden="true">
            ▾
          </span>
        </button>
      </div>
      {menuNode}
    </>
  );
}

/** ---- stable "last season status wins" ---- */
function useSeq() {
  const seqRef = useRef(0);
  const next = () => {
    seqRef.current += 1;
    return seqRef.current;
  };
  const isLatest = (seq) => seq === seqRef.current;
  return { next, isLatest };
}

/** ✅ create eventDate inside season year */
function eventDateForSeasonYear(seasonYear) {
  const y = Number(seasonYear);
  const now = new Date();
  const mm = now.getMonth() + 1;
  const dd = now.getDate();

  const lastDay = new Date(y, mm, 0).getDate();
  const safeDay = Math.min(dd, lastDay);

  return `${y}-${pad2(mm)}-${pad2(safeDay)}`;
}

/** =========================
    CROPS
   ========================= */
function cropImgSrc(c) {
  if (!c) return "";
  if (c?.imageUrl) return String(c.imageUrl);
  // ✅ ВИПРАВЛЕНО: підтримка обох варіантів на всяк випадок
  if (c?.image_url) return String(c.image_url);
  const id = c?.id != null ? String(c.id) : "";
  return id ? `/crops/crop-${id}.png` : "";
}

function cropDisplayName(c) {
  // ✅ ВИПРАВЛЕНО: nameEn замість name_en
  return String(c?.name || c?.nameEn || c?.name_en || "").trim() || "—";
}

function cropSubline(c) {
  const parts = [];
  if (c?.nameEn) parts.push(String(c.nameEn));
  else if (c?.name_en) parts.push(String(c.name_en));
  if (c?.category) parts.push(categoryTitle(c.category));
  return parts.join(" • ");
}

function seasonCropId(season) {
  const sid = season?.cropId ?? season?.crop?.id ?? null;
  return sid == null ? "" : String(sid);
}
function resolveSeasonCrop(season, crops) {
  const id = seasonCropId(season);
  if (id && Array.isArray(crops)) {
    const found = crops.find((c) => String(c?.id) === id);
    if (found) return found;
  }
  if (season?.crop && typeof season.crop === "object") return season.crop;
  return null;
}

const CATEGORY_UA = {
  GRAINS: "Зернові",
  OILSEEDS: "Олійні",
  LEGUMES: "Бобові",
  VEGETABLES: "Овочі",
  FRUITS: "Фрукти",
  INDUSTRIAL: "Технічні",
  FORAGE: "Кормові",
  OTHER: "Інші",
};

function categoryTitle(cat) {
  const k = String(cat || "").trim();
  return CATEGORY_UA[k] || k || "—";
}
function categoryImgSrc(cat) {
  const k = String(cat || "").trim();
  return k ? `/crop-categories/${k}.png` : "";
}

function CropPicker({
  crops,
  loading,
  category,
  onCategory,
  cropId,
  onCropId,
  disabled,
}) {
  const cats = useMemo(() => {
    const set = new Set();
    (Array.isArray(crops) ? crops : []).forEach((c) => {
      const k = String(c?.category ?? "").trim();
      if (k) set.add(k);
    });
    const arr = [...set].filter((x) => ALLOWED_CROP_CATEGORIES.has(x));
    arr.sort((a, b) => a.localeCompare(b));
    return arr;
  }, [crops]);

  const selected = useMemo(() => {
    const id = cropId == null ? "" : String(cropId).trim();
    if (!id) return null;
    return (
      (Array.isArray(crops) ? crops : []).find((c) => String(c?.id) === id) ||
      null
    );
  }, [crops, cropId]);

  const BLOCKED_CROPS = new Set(["Industrial hemp", "Tobacco"]);

  const filtered = useMemo(() => {
    const list = Array.isArray(crops) ? crops : [];
    const cat = String(category || "").trim();
    if (!cat) return [];
    return list.filter((c) =>
      String(c?.category || "") === cat &&
      !BLOCKED_CROPS.has(c?.nameEn ?? c?.name_en ?? "")
    );
  }, [crops, category]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="fd-cropPick">
      <div className="fd-lbl">Категорії</div>

      {loading ? (
        <div className="fd-hint">Завантажую категорії</div>
      ) : (
        <div className="fd-catGrid">
          {cats.map((k) => {
            const active = String(category || "") === String(k);
            const img = categoryImgSrc(k);
            return (
              <button
                type="button"
                key={k}
                className={`fd-catTile ${active ? "isActive" : ""}`}
                onClick={() => {
                  onCategory?.(k);
                  onCropId?.("");
                }}
                disabled={disabled}
                title={categoryTitle(k)}
              >
                <span className="fd-catImg">
                  {img ? (
                    <img
                      src={img}
                      alt={categoryTitle(k)}
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                        if (e.currentTarget.nextSibling) e.currentTarget.nextSibling.style.display = "flex";
                      }}
                    />
                  ) : null}
                  {img
                    ? <span className="fd-cropImgFallback" style={{ display: "none" }}>🌾</span>
                    : <span className="fd-cropImgFallback">🌾</span>}
                </span>
                <span className="fd-catText">{categoryTitle(k)}</span>
              </button>
            );
          })}
        </div>
      )}

      <div style={{ marginTop: 12 }}>
        <div className="fd-lbl">Культури</div>

        {!category ? (
          <div className="fd-hint">Спочатку вибери категорію зверху</div>
        ) : loading ? (
          <div className="fd-hint">Завантажую культури...</div>
        ) : filtered.length === 0 ? (
          <div className="fd-hint">Немає культур у цій категорії.</div>
        ) : (
          <>
            <div className="fd-cropSelected" data-has={selected ? "1" : "0"}>
              {selected ? (
                <>
                  <span className="fd-cropThumb">
                    {cropImgSrc(selected) ? (
                      <img
                        src={cropImgSrc(selected)}
                        alt={cropDisplayName(selected)}
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                          if (e.currentTarget.nextSibling) e.currentTarget.nextSibling.style.display = "flex";
                        }}
                      />
                    ) : null}
                    {!cropImgSrc(selected) && <span className="fd-cropImgFallback">🌱</span>}
                    {cropImgSrc(selected) && <span className="fd-cropImgFallback" style={{ display: "none" }}>🌱</span>}
                  </span>

                  <div className="fd-cropSelectedText">
                    <div
                      className="fd-cropSelectedName"
                      title={cropDisplayName(selected)}
                    >
                      {cropDisplayName(selected)}
                    </div>
                    <div className="fd-cropSelectedSub">
                      {/* ✅ ВИПРАВЛЕНО: nameEn замість name_en */}
                      {cropSubline(selected) || `id: ${selected?.id ?? "—"}`}
                    </div>
                  </div>

                  <button
                    type="button"
                    className="btn"
                    onClick={() => onCropId?.("")}
                    disabled={disabled}
                    style={{ height: 40 }}
                  >
                    Очистити
                  </button>
                </>
              ) : (
                <div className="fd-hint" style={{ margin: 0 }}>
                  Виберіть культуру нижче.
                </div>
              )}
            </div>

            <div className="fd-cropTiles">
              {filtered.map((c) => {
                const is = String(c?.id) === String(cropId || "");
                const name = cropDisplayName(c);
                const img = cropImgSrc(c);

                return (
                  <button
                    type="button"
                    key={String(c?.id)}
                    className={`fd-cropTile2 ${is ? "isActive" : ""}`}
                    onClick={() => onCropId?.(String(c?.id))}
                    disabled={disabled}
                    title={name}
                  >
                    <span className="fd-cropTileImg">
                      {img ? (
                        <img
                          src={img}
                          alt={name}
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                            if (e.currentTarget.nextSibling) e.currentTarget.nextSibling.style.display = "flex";
                          }}
                        />
                      ) : null}
                      {(!img) && <span className="fd-cropImgFallback">🌱</span>}
                      {img && <span className="fd-cropImgFallback" style={{ display: "none" }}>🌱</span>}
                    </span>

                    <span className="fd-cropTileText">
                      <span className="fd-cropTileName">{name}</span>
                      {/* ✅ ВИПРАВЛЕНО: nameEn замість name_en */}
                      <span className="fd-cropTileSub">
                        {c?.nameEn ? String(c.nameEn) : (c?.name_en ? String(c.name_en) : "")}
                      </span>
                    </span>

                    <span className="fd-cropTick">{is ? "✓" : ""}</span>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** =========================
    season date range
   ========================= */
function parseISODateOnly(s) {
  if (!s) return null;
  const str = String(s).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    const d = new Date(String(s));
    return Number.isFinite(d.getTime()) ? d : null;
  }
  const [y, m, d] = str.split("-").map((x) => Number(x));
  const dt = new Date(y, (m || 1) - 1, d || 1);
  return Number.isFinite(dt.getTime()) ? dt : null;
}
function dayOfYear(d) {
  const start = new Date(d.getFullYear(), 0, 1);
  const diff = d.getTime() - start.getTime();
  const oneDay = 24 * 60 * 60 * 1000;
  return Math.floor(diff / oneDay) + 1;
}
function daysInYear(y) {
  const a = new Date(Number(y), 0, 1);
  const b = new Date(Number(y) + 1, 0, 1);
  return Math.round((b - a) / (24 * 60 * 60 * 1000));
}
function clamp01(x) {
  return Math.max(0, Math.min(1, x));
}

function SeasonRange({ year, sowingDate, harvestDate }) {
  const y = Number(year);
  const sow = parseISODateOnly(sowingDate);
  const harv = parseISODateOnly(harvestDate);

  const total = Number.isFinite(y) ? daysInYear(y) : 365;

  function pctFor(d) {
    if (!d || !Number.isFinite(d.getTime()) || !Number.isFinite(y)) return null;
    const dy = dayOfYear(d);
    return clamp01((dy - 1) / Math.max(1, total - 1));
  }

  const sowPct = pctFor(sow);
  const harvPct = pctFor(harv);

  let left = null;
  let width = null;
  if (sowPct != null && harvPct != null) {
    const a = Math.min(sowPct, harvPct);
    const b = Math.max(sowPct, harvPct);
    left = a;
    width = Math.max(0.02, b - a);
  } else if (sowPct != null) {
    left = sowPct;
    width = 0.03;
  } else if (harvPct != null) {
    left = harvPct;
    width = 0.03;
  }

  return (
    <div className="fd-seasonRange">
      <div className="fd-seasonRangeTop">
        <div className="fd-seasonRangeLabel">
          <span className="fd-seasonRangeK">Діапазон:</span>{" "}
          <span className="fd-seasonRangeV">
            {sow ? fmtDate(sow.toISOString()) : "—"}{" "}
            <span style={{ opacity: 0.65 }}>→</span>{" "}
            {harv ? fmtDate(harv.toISOString()) : "—"}
          </span>
        </div>
        <div className="fd-seasonRangeMini">
          <span className="fd-seasonRangeMiniPill">
            {sow ? `Посів: ${fmtDate(sow.toISOString())}` : "Посів: —"}
          </span>
          <span className="fd-seasonRangeMiniPill">
            {harv ? `Збір: ${fmtDate(harv.toISOString())}` : "Збір: —"}
          </span>
        </div>
      </div>

      <div className="fd-seasonRangeBar" aria-hidden="true">
        <div className="fd-seasonRangeBarBg" />
        {left != null && width != null ? (
          <div
            className="fd-seasonRangeBarSeg"
            style={{
              left: `${Math.round(left * 1000) / 10}%`,
              width: `${Math.round(width * 1000) / 10}%`,
            }}
          />
        ) : null}
        <div className="fd-seasonRangeBarTicks">
          <span className="t" />
          <span className="t" />
          <span className="t" />
          <span className="t" />
        </div>
        <div className="fd-seasonRangeBarLegend">
          <span>Січ</span>
          <span>Квіт</span>
          <span>Лип</span>
          <span>Жовт</span>
        </div>
      </div>
    </div>
  );
}

export default function FieldDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [field, setField] = useState(null);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  const [tab, setTab] = useState("overview");

  /** field edit/delete */
  const [fieldEditOpen, setFieldEditOpen] = useState(false);
  const [fieldSaving, setFieldSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingField, setDeletingField] = useState(false);

  /** contour edit */
  const [contourEditOpen, setContourEditOpen] = useState(false);
  const [contourSaving, setContourSaving] = useState(false);
  const [editedGeometry, setEditedGeometry] = useState(null);

  const [efName, setEfName] = useState("");
  const [efArea, setEfArea] = useState("");
  const [efNote, setEfNote] = useState("");
  const [efGroupId, setEfGroupId] = useState("");

  const [seasonsLoading, setSeasonsLoading] = useState(false);
  const [activeSeason, setActiveSeason] = useState(null);
  const [seasons, setSeasons] = useState([]);

  const [historyReloadKey, setHistoryReloadKey] = useState(0);

  const [cropsLoading, setCropsLoading] = useState(false);
  const [crops, setCrops] = useState([]);

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editSeasonObj, setEditSeasonObj] = useState(null);

  const nowYear = new Date().getFullYear();
  const yearOptions = useMemo(() => {
    const out = [];
    for (let y = nowYear + 1; y >= nowYear - 10; y--) out.push(String(y));
    return out;
  }, [nowYear]);

  // create form
  const [sYear, setSYear] = useState(String(nowYear));
  const [sSeasonName, setSSeasonName] = useState("");
  const [sCropCategory, setSCropCategory] = useState("");
  const [sCropId, setSCropId] = useState("");
  const [varietyName, setVarietyName] = useState("");
  const [seasonField, setSeasonField] = useState("PLANNED");
  const [sowingDate, setSowingDate] = useState("");
  const [harvestDate, setHarvestDate] = useState("");
  const [notes, setNotes] = useState("");

  // edit form
  const [eYear, setEYear] = useState(String(nowYear));
  const [eSeasonName, setESeasonName] = useState("");
  const [eCropCategory, setECropCategory] = useState("");
  const [eCropId, setECropId] = useState("");
  const [eVarietyName, setEVarietyName] = useState("");
  const [eSeasonField, setESeasonField] = useState("PLANNED");
  const [eSowingDate, setESowingDate] = useState("");
  const [eHarvestDate, setEHarvestDate] = useState("");
  const [eNotes, setENotes] = useState("");

  const [activatingYear, setActivatingYear] = useState(null);

  const [statusSavingKey, setStatusSavingKey] = useState(null);
  const seasonPatchSeq = useSeq();

  const seasonCarouselRef = useRef(null);

  useEffect(() => {
    const u = getStoredUser();
    if (!u) {
      navigate("/login");
      return;
    }
    setUser(u);
  }, [navigate]);

  function toast(type, text) {
    setMsg({ type, text });
    window.clearTimeout(toast._t);
    toast._t = window.setTimeout(() => setMsg(null), 2600);
  }

  const bbox = useMemo(() => {
    if (!field) return null;
    const minLng = field.bboxMinLng,
      minLat = field.bboxMinLat,
      maxLng = field.bboxMaxLng,
      maxLat = field.bboxMaxLat;
    if (
      [minLng, minLat, maxLng, maxLat].every((x) => Number.isFinite(Number(x)))
    ) {
      return [Number(minLng), Number(minLat), Number(maxLng), Number(maxLat)];
    }
    return computeBBoxFromGeometry(field?.geometry);
  }, [field]);

  const currentGeometry = useMemo(() => {
    return (
      field?.geometry ??
      parseMaybeJson(field?.geometryJson) ??
      parseMaybeJson(field?.contour) ??
      null
    );
  }, [field]);

  const previousGeometry = useMemo(() => {
    try {
      const raw = localStorage.getItem(`agro:prev_geom:${id}`);
      if (!raw) return null;
      const geo = JSON.parse(raw);
      // Не показуємо якщо збігається з поточним
      if (JSON.stringify(geo) === JSON.stringify(currentGeometry)) return null;
      return geo;
    } catch { return null; }
  }, [id, currentGeometry]);

  const currentGroupName = useMemo(() => {
    const gid = String(field?.fieldGroupId ?? "");
    const found = groups.find((g) => String(g?.id) === gid);
    return found?.name || (gid ? `#${gid}` : "—");
  }, [field, groups]);

  async function loadGroups() {
    if (!user) return;
    const res = await apiFetch(user, "/api/field-groups", { method: "GET" }, navigate);
    if (!res.ok) return;
    const arr = await res.json().catch(() => []);
    setGroups(Array.isArray(arr) ? arr : []);
  }

  async function loadField() {
    if (!user) return;
    setLoading(true);
    try {
      const res = await apiFetch(user, "/api/fields", { method: "GET" }, navigate);
      if (!res.ok) {
        const e = await safeReadError(res);
        toast("err", e || `Помилка (${res.status})`);
        setField(null);
        return;
      }
      const arr = (await res.json()) || [];
      const f = Array.isArray(arr)
        ? arr.find((x) => String(x.id) === String(id))
        : null;

      const normalized = normalizeFieldFromApi(f);
      setField(normalized || null);
      if (!normalized) toast("err", "Поле не знайдено");
    } finally {
      setLoading(false);
    }
  }

  async function loadSeasons() {
    if (!user) return;
    setSeasonsLoading(true);
    try {
      const resAll = await apiFetch(
        user,
        `/api/fields/${id}/seasons`,
        { method: "GET" },
        navigate
      );
      if (!resAll.ok) {
        const e = await safeReadError(resAll);
        toast("err", e || `Помилка сезонів (${resAll.status})`);
        setSeasons([]);
        setActiveSeason(null);
        return;
      }
      const list = (await resAll.json()) || [];
      setSeasons(Array.isArray(list) ? list : []);

      const resAct = await apiFetch(
        user,
        `/api/fields/${id}/seasons/active`,
        { method: "GET" },
        navigate
      );
      if (resAct.ok) {
        setActiveSeason(await resAct.json());
      } else {
        const found = Array.isArray(list) ? list.find((x) => x && x.active) : null;
        setActiveSeason(found || null);
      }
    } finally {
      setSeasonsLoading(false);
    }
  }

  async function loadCrops() {
  if (!user) return;
  setCropsLoading(true);
  try {
    const res = await apiFetch(user, "/api/crop", { method: "GET" }, navigate);
    if (!res.ok) {
      const t = await safeReadError(res);
      toast("err", t || `Не вдалося завантажити культури (${res.status})`);
      setCrops([]);
      return;
    }
    const data = await res.json();
    let list = Array.isArray(data) ? data : (data?.content ?? []);
    const filtered = list
      .filter((c) => isAllowedCrop(c))
      .sort((a, b) => {
        const ca = String(a?.category ?? "");
        const cb = String(b?.category ?? "");
        if (ca !== cb) return ca.localeCompare(cb);
        const ea = String(a?.nameEn ?? a?.name_en ?? "");
        const eb = String(b?.nameEn ?? b?.name_en ?? "");
        if (ea !== eb) return ea.localeCompare(eb);
        return String(a?.name ?? "").localeCompare(String(b?.name ?? ""));
      });
    setCrops(filtered);
  } finally {
    setCropsLoading(false);
  }
}

  useEffect(() => {
    if (!user) return;
    loadField().catch(() => {});
    loadGroups().catch(() => {});
  }, [user, id]);

  useEffect(() => {
    if (!user) return;
    loadSeasons().catch(() => {});
  }, [user, id]);

  useEffect(() => {
    if (!user) return;
    loadCrops().catch(() => {});
  }, [user]);

  const seasonsSorted = useMemo(() => {
    const arr = Array.isArray(seasons) ? [...seasons] : [];
    arr.sort((a, b) => Number(b.year || 0) - Number(a.year || 0));
    return arr;
  }, [seasons]);

  const activeTone = statusTone(activeSeason?.seasonField);

  const fieldTone = useMemo(() => {
    if (!field) return "muted";
    if (currentGeometry) return "ok";
    return "muted";
  }, [field, currentGeometry]);

  const fieldStats = useMemo(() => {
    const ha = field?.areaHa;
    const contour = !!currentGeometry;
    return {
      haText: ha == null ? "—" : `${nf(ha, 2)} ha`,
      contourText: contour ? "Є контур ✅" : "Нема контуру —",
      groupText: currentGroupName,
      noteText: emptyDash(field?.note),
    };
  }, [field, currentGeometry, currentGroupName]);

  function openFieldEdit() {
    if (!field) return;
    setEfName(String(field.name ?? ""));
    setEfArea(field.areaHa == null ? "" : String(field.areaHa));
    setEfNote(String(field.note ?? ""));
    setEfGroupId(field.fieldGroupId == null ? "" : String(field.fieldGroupId));
    setFieldEditOpen(true);
  }

  function closeFieldEdit() {
    if (fieldSaving) return;
    setFieldEditOpen(false);
  }

  function openContourEdit() {
    if (!field) return;
    setEditedGeometry(currentGeometry || null);
    setContourEditOpen(true);
  }

  function closeContourEdit() {
    if (contourSaving) return;
    setContourEditOpen(false);
  }

  async function handleSaveField(e) {
    e.preventDefault();
    if (!field || fieldSaving) return;

    const name = String(efName || "").trim();
    if (!name) {
      toast("err", "Введи назву поля");
      return;
    }

    const area = Number(efArea);
    if (!Number.isFinite(area) || area <= 0) {
      toast("err", "Площа має бути більше 0");
      return;
    }

    if (!efGroupId) {
      toast("err", "Обери групу");
      return;
    }

    const geometry =
      currentGeometry ??
      parseMaybeJson(field?.geometryJson) ??
      parseMaybeJson(field?.geometry) ??
      null;

    if (!geometry) {
      toast("err", "Не знайдено geometry для оновлення поля");
      return;
    }

    const payload = {
      name,
      areaHa: area,
      note: String(efNote || "").trim(),
      fieldGroupId: Number(efGroupId),
      geometry,
    };

    setFieldSaving(true);
    try {
      const res = await apiFetch(
        user,
        `/api/fields/${id}`,
        { method: "PUT", body: JSON.stringify(payload) },
        navigate
      );

      if (!res.ok) {
        const t = await safeReadError(res);
        toast("err", t || `Не вдалося оновити поле (${res.status})`);
        return;
      }

      const updated = await res.json().catch(() => null);
      const normalized = normalizeFieldFromApi(updated);

      if (normalized) {
        setField((prev) => ({
          ...(prev || {}),
          ...normalized,
          geometry:
            normalized.geometry ??
            currentGeometry ??
            prev?.geometry ??
            null,
        }));
      }

      setFieldEditOpen(false);
      toast("ok", "Поле оновлено ✅");
      await loadField();
      await loadGroups();
    } finally {
      setFieldSaving(false);
    }
  }

  async function handleSaveContour() {
    if (!field || contourSaving) return;

    if (!editedGeometry || getGeometryOnly(editedGeometry)?.type !== "Polygon") {
      toast("err", "Потрібно намалювати або відредагувати полігон");
      return;
    }

    const payload = {
      name: String(field.name ?? "").trim(),
      areaHa: Number(field.areaHa ?? 0),
      note: String(field.note ?? "").trim(),
      fieldGroupId: Number(field.fieldGroupId),
      geometry: editedGeometry,
    };

    if (!payload.name) {
      toast("err", "У поля немає назви");
      return;
    }
    if (!Number.isFinite(payload.areaHa) || payload.areaHa <= 0) {
      toast("err", "У поля некоректна площа");
      return;
    }
    if (!Number.isFinite(payload.fieldGroupId) || payload.fieldGroupId <= 0) {
      toast("err", "У поля некоректна група");
      return;
    }

    // Зберігаємо поточну геометрію як "попередню" перед оновленням
    if (currentGeometry) {
      try { localStorage.setItem(`agro:prev_geom:${id}`, JSON.stringify(currentGeometry)); } catch {}
    }

    setContourSaving(true);
    try {
      const res = await apiFetch(
        user,
        `/api/fields/${id}`,
        {
          method: "PUT",
          body: JSON.stringify(payload),
        },
        navigate
      );

      if (!res.ok) {
        const t = await safeReadError(res);
        toast("err", t || `Не вдалося зберегти контур (${res.status})`);
        return;
      }

      const updated = await res.json().catch(() => null);
      const normalized = normalizeFieldFromApi(updated);

      const fallbackBBox = computeBBoxFromGeometry(editedGeometry);

      setField((prev) => ({
        ...(prev || {}),
        ...(normalized || {}),
        geometry: normalized?.geometry ?? editedGeometry,
        bboxMinLng:
          normalized?.bboxMinLng ??
          (fallbackBBox ? fallbackBBox[0] : prev?.bboxMinLng),
        bboxMinLat:
          normalized?.bboxMinLat ??
          (fallbackBBox ? fallbackBBox[1] : prev?.bboxMinLat),
        bboxMaxLng:
          normalized?.bboxMaxLng ??
          (fallbackBBox ? fallbackBBox[2] : prev?.bboxMaxLng),
        bboxMaxLat:
          normalized?.bboxMaxLat ??
          (fallbackBBox ? fallbackBBox[3] : prev?.bboxMaxLat),
      }));

      setContourEditOpen(false);
      toast("ok", "Контур оновлено ✅");
      await loadField();
    } finally {
      setContourSaving(false);
    }
  }

  async function handleDeleteField() {
    if (!field || deletingField) return;

    setDeletingField(true);
    try {
      const res = await apiFetch(
        user,
        `/api/fields/${id}`,
        { method: "DELETE" },
        navigate
      );

      if (!res.ok) {
        const t = await safeReadError(res);
        toast("err", t || `Не вдалося видалити поле (${res.status})`);
        return;
      }

      toast("ok", "Поле видалено ✅");
      setDeleteOpen(false);
      navigate("/fields");
    } finally {
      setDeletingField(false);
    }
  }

  function openCreateSeason() {
    setSYear(String(nowYear));
    setSSeasonName("");
    setSCropCategory("");
    setSCropId("");
    setVarietyName("");
    setSeasonField("PLANNED");
    setSowingDate("");
    setHarvestDate("");
    setNotes("");
    setCreateOpen(true);
  }
  function closeCreateSeason() {
    if (creating) return;
    setCreateOpen(false);
  }

  function openEditSeason(season) {
    if (!season) return;
    setEditSeasonObj(season);

    setEYear(String(season.year ?? nowYear));
    setESeasonName(String(season.name ?? ""));
    setEVarietyName(String(season.varietyName ?? ""));
    setESeasonField(String(season.seasonField ?? "PLANNED"));
    setESowingDate(season.sowingDate ? String(season.sowingDate).slice(0, 10) : "");
    setEHarvestDate(season.harvestDate ? String(season.harvestDate).slice(0, 10) : "");
    setENotes(String(season.notes ?? ""));

    const sid = season?.cropId ?? season?.crop?.id ?? null;
    const sidStr = sid == null ? "" : String(sid);

    let found = null;
    if (!sidStr && season?.cropName && Array.isArray(crops)) {
      found = crops.find(
        (c) =>
          String(c?.name || "").trim().toLowerCase() ===
          String(season.cropName).trim().toLowerCase()
      );
    }

    const finalId = sidStr || (found?.id != null ? String(found.id) : "");
    setECropId(finalId);

    const picked = finalId
      ? crops.find((c) => String(c?.id) === String(finalId))
      : null;
    setECropCategory(picked?.category ? String(picked.category) : "");

    setEditOpen(true);
  }
  function closeEditSeason() {
    if (editing) return;
    setEditOpen(false);
    setEditSeasonObj(null);
  }

  async function logStatusChangeEvent(season, fromStatus, toStatus) {
    if (!user) return { ok: false, text: "No user" };
    if (!season?.id || !season?.year) return { ok: false, text: "No season id/year" };

    const from = String(fromStatus ?? "").trim() || null;
    const to = String(toStatus ?? "").trim() || null;

    if ((from || "") === (to || "")) return { ok: true, skipped: true };

    const title = from
      ? `Зміна статусу сезону: ${from} → ${to || "—"}`
      : `Встановлено статус сезону: ${to || "—"}`;

    const body = {
      seasonId: season.id,
      type: "STATUS_CHANGE",
      eventDate: eventDateForSeasonYear(season.year),
      title,
      description: null,
      dataJson: JSON.stringify({ from, to }),
    };

    const res = await apiFetch(
      user,
      `/api/fields/${id}/events`,
      { method: "POST", body: JSON.stringify(body) },
      navigate
    );
    if (!res.ok) {
      return { ok: false, status: res.status, text: await safeReadError(res) };
    }
    return { ok: true };
  }

  async function handleCreateSeason(e) {
    e.preventDefault();
    if (creating) return;

    const yearNum = Number(String(sYear).trim());
    if (!Number.isFinite(yearNum) || yearNum < 2000 || yearNum > 2100) {
      toast("err", "Обери коректний рік");
      return;
    }

    const cropIdNum = Number(String(sCropId || "").trim());
    if (!Number.isFinite(cropIdNum) || cropIdNum <= 0) {
      toast("err", "Культура обов'язкова (обери культуру)");
      return;
    }

    const variety = varietyName.trim();
    if (variety.length < 2) return toast("err", "Сорт: мінімум 2 символи");

    const body = {
      year: yearNum,
      name: sSeasonName.trim() || null,
      cropId: cropIdNum,
      varietyName: variety,
      seasonField: String(seasonField || "PLANNED").trim(),
      sowingDate: sowingDate || null,
      harvestDate: harvestDate || null,
      notes: notes.trim() || null,
    };

    setCreating(true);
    try {
      const res = await apiFetch(
        user,
        `/api/fields/${id}/seasons`,
        { method: "POST", body: JSON.stringify(body) },
        navigate
      );
      if (!res.ok) {
        const t = await safeReadError(res);
        toast("err", t || `Не вдалося створити сезон (${res.status})`);
        return;
      }

      const createdSeason = await res.json();

      const logRes = await logStatusChangeEvent(
        createdSeason,
        null,
        createdSeason?.seasonField || body.seasonField
      );
      if (!logRes?.ok) {
        toast(
          "err",
          `Сезон створено, але не вдалось записати історію статусу: ${
            logRes?.text || "?"
          }`
        );
      } else {
        toast("ok", "Сезон створено ✅");
      }

      setCreateOpen(false);
      await loadSeasons();
      setHistoryReloadKey((k) => k + 1);
    } finally {
      setCreating(false);
    }
  }

  async function patchSeasonByYear(year, patch) {
    if (!user) return { ok: false, status: 0, text: "No user" };

    const y = String(year ?? "").trim();
    if (!/^\d+$/.test(y)) return { ok: false, status: 0, text: "Bad year" };

    const url = `/api/fields/${id}/seasons/${y}`;

    let res = await apiFetch(
      user,
      url,
      { method: "PATCH", body: JSON.stringify(patch) },
      navigate
    );
    if (res.ok) return { ok: true, status: res.status, url };

    if (res.status === 405) {
      res = await apiFetch(
        user,
        url,
        { method: "PUT", body: JSON.stringify(patch) },
        navigate
      );
      if (res.ok) return { ok: true, status: res.status, url, used: "PUT" };
    }

    return { ok: false, status: res.status, text: await safeReadError(res), url };
  }

  async function handleSaveEditSeason(e) {
    e.preventDefault();
    if (editing || !editSeasonObj) return;

    const origYear = Number(editSeasonObj.year);
    const uiYear = Number(String(eYear).trim());
    if (Number.isFinite(uiYear) && uiYear !== origYear) {
      toast(
        "err",
        "Рік сезону змінювати не можна. Створи новий сезон на інший рік."
      );
      setEYear(String(origYear));
      return;
    }

    const cropIdNum = Number(String(eCropId || "").trim());
    if (!Number.isFinite(cropIdNum) || cropIdNum <= 0) {
      toast("err", "Культура обов'язкова (обери культуру)");
      return;
    }

    const variety = clampStr(eVarietyName, 80);
    if (variety.length < 2) return toast("err", "Сорт: мінімум 2 символи");

    const nextStatus = String(eSeasonField || "PLANNED").trim();
    const prevStatus = String(editSeasonObj?.seasonField || "PLANNED").trim();

    const patch = {
      name: eSeasonName.trim() || null,
      cropId: cropIdNum,
      varietyName: variety,
      seasonField: nextStatus,
      sowingDate: eSowingDate || null,
      harvestDate: eHarvestDate || null,
      notes: clampStr(eNotes, 700) || null,
    };

    setEditing(true);
    try {
      setSeasons((prev) =>
        (Array.isArray(prev) ? prev : []).map((s) => {
          const same =
            s?.year != null &&
            editSeasonObj?.year != null &&
            String(s.year) === String(editSeasonObj.year);
          return same ? { ...s, ...patch } : s;
        })
      );

      if (activeSeason) {
        const sameActive =
          activeSeason?.year != null &&
          editSeasonObj?.year != null &&
          String(activeSeason.year) === String(editSeasonObj.year);
        if (sameActive) setActiveSeason((x) => ({ ...(x || {}), ...patch }));
      }

      const res = await patchSeasonByYear(editSeasonObj.year, patch);
      if (!res?.ok) {
        toast("err", res?.text || `Не вдалося зберегти (${res?.status || "?"})`);
        await loadSeasons();
        return;
      }

      if (!sameStr(prevStatus, nextStatus)) {
        const seasonForLog = { ...editSeasonObj, ...patch };
        const logRes = await logStatusChangeEvent(
          seasonForLog,
          prevStatus,
          nextStatus
        );
        if (!logRes?.ok) {
          toast(
            "err",
            `Збережено, але історію статусу не записано: ${logRes?.text || "?"}`
          );
        }
        setHistoryReloadKey((k) => k + 1);
      }

      toast("ok", "Збережено ✅");
      setEditOpen(false);
      setEditSeasonObj(null);
      await loadSeasons();
    } finally {
      setEditing(false);
    }
  }

  async function quickChangeStatus(season, newStatus) {
    if (!season || !newStatus) return;

    const { kind, key } = pickSeasonKey(season);
    const lockKey = `${kind}:${key || season.year || ""}`;

    if (statusSavingKey === lockKey) return;

    const seq = seasonPatchSeq.next();
    setStatusSavingKey(lockKey);

    const prevStatus = String(season?.seasonField || "PLANNED");
    const nextStatus = String(newStatus || "PLANNED");

    setSeasons((prev) =>
      (Array.isArray(prev) ? prev : []).map((s) => {
        const same =
          s?.year != null &&
          season?.year != null &&
          String(s.year) === String(season.year);
        return same ? { ...s, seasonField: nextStatus } : s;
      })
    );
    if (activeSeason) {
      const sameActive =
        activeSeason?.year != null &&
        season?.year != null &&
        String(activeSeason.year) === String(season.year);
      if (sameActive)
        setActiveSeason((x) => ({ ...(x || {}), seasonField: nextStatus }));
    }

    try {
      const res = await patchSeasonByYear(season.year, { seasonField: nextStatus });

      if (!seasonPatchSeq.isLatest(seq)) return;

      if (!res?.ok) {
        toast(
          "err",
          res?.text || `Не вдалося змінити статус (${res?.status || "?"})`
        );
        await loadSeasons();
        return;
      }

      const logRes = await logStatusChangeEvent(season, prevStatus, nextStatus);
      if (!logRes?.ok) {
        toast(
          "err",
          `Статус оновлено, але історію не записано: ${logRes?.text || "?"}`
        );
      } else {
        toast("ok", "Статус оновлено ✅");
      }

      setHistoryReloadKey((k) => k + 1);
      await loadSeasons();
    } finally {
      if (seasonPatchSeq.isLatest(seq)) setStatusSavingKey(null);
    }
  }

  async function activateSeason(year) {
    if (!user) return;
    if (activatingYear) return;

    const y = String(year ?? "").trim();
    if (!/^\d+$/.test(y)) return toast("err", "Некоректний рік");

    setActivatingYear(year);
    try {
      const res = await apiFetch(
        user,
        `/api/fields/${id}/seasons/${y}/activate`,
        { method: "PATCH" },
        navigate
      );
      if (!res.ok) {
        const t = await safeReadError(res);
        toast("err", t || `Не вдалося активувати (${res.status})`);
        return;
      }
      toast("ok", `Активний сезон: ${year} ✅`);
      await loadSeasons();
    } finally {
      setActivatingYear(null);
    }
  }

  const activeSummary = useMemo(() => {
    if (!activeSeason) return null;
    const cropObj = resolveSeasonCrop(activeSeason, crops);
    const cropName = cropObj
      ? cropDisplayName(cropObj)
      : emptyDash(activeSeason?.cropName || activeSeason?.crop?.name);
    const cropImg = cropObj
      ? cropImgSrc(cropObj)
      : seasonCropId(activeSeason)
      ? `/crops/crop-${seasonCropId(activeSeason)}.png`
      : "";
    const cropSub = cropObj ? cropSubline(cropObj) : "";
    return {
      year: activeSeason?.year ?? "—",
      crop: cropName,
      cropImg,
      cropSub,
      variety: emptyDash(activeSeason?.varietyName),
      status: seasonFieldLabel(activeSeason?.seasonField),
      sow: fmtDate(activeSeason?.sowingDate),
      harv: fmtDate(activeSeason?.harvestDate),
      note: activeSeason?.notes ? String(activeSeason.notes) : "",
      sowRaw: activeSeason?.sowingDate || null,
      harvRaw: activeSeason?.harvestDate || null,
    };
  }, [activeSeason, crops]);

  function scrollSeasons(dx) {
    const el = seasonCarouselRef.current;
    if (!el) return;
    el.scrollBy({ left: dx, behavior: "smooth" });
  }

  return (
    <AppShell
      title={field?.name || `Поле #${id}`}
      right={
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button className="btn" onClick={() => navigate(-1)} type="button">
            ← Назад
          </button>
        </div>
      }
    >
      <div className="fd-theme">
        <style>{`
          .fd-theme{
            --fd-bg: var(--bg);
            --fd-panel: var(--panel);
            --fd-panel-2: var(--panel2);
            --fd-soft: var(--surface-soft);
            --fd-card: var(--panel);
            --fd-border: var(--border);
            --fd-border-strong: var(--border-strong);
            --fd-text: var(--text);
            --fd-muted: var(--muted);
            --fd-muted-2: var(--muted);
            --fd-shadow: var(--shadow);
            --fd-ok-bg: rgba(34,197,94,0.14);
            --fd-ok-br: rgba(34,197,94,0.38);
            --fd-warn-bg: rgba(251,191,36,0.14);
            --fd-warn-br: rgba(251,191,36,0.38);
            --fd-danger-bg: rgba(239,68,68,0.16);
            --fd-danger-br: rgba(239,68,68,0.35);
            --fd-info-bg: rgba(59,130,246,0.14);
            --fd-info-br: rgba(59,130,246,0.35);
            color: var(--fd-text);
          }

          .fd-theme .panel,
          .fd-theme .infoCard,
          .fd-theme .modalCard{
            color: var(--fd-text);
          }

          .fd-toastBox{
            position: fixed;
            right: 16px;
            top: 16px;
            z-index: 200;
            padding: 10px 12px;
            border-radius: 14px;
            background: var(--fd-panel-2);
            backdrop-filter: blur(14px);
            box-shadow: var(--fd-shadow);
            max-width: 420px;
            border: 1px solid var(--fd-border);
          }

          .fd-sectionHead{ display:flex; gap:12px; justify-content:space-between; align-items:flex-start; flex-wrap:wrap; }
          .fd-sectionTitle{ font-weight: 980; font-size: 18px; letter-spacing: .01em; color: var(--fd-text); }
          .fd-sectionSub{ margin-top: 2px; color: var(--fd-muted); font-weight: 700; font-size: 13px; }
          .fd-sectionRight{ display:flex; gap:10px; flex-wrap:wrap; }

          .fd-tabs{
            margin-top: 12px;
            display:flex;
            gap: 8px;
            flex-wrap: nowrap;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            padding-bottom: 4px;
            scrollbar-width: none;
          }
          .fd-tabs::-webkit-scrollbar{ display: none; }
          .fd-tabBtn{
            height: 44px; padding: 0 16px; border-radius: 999px; flex-shrink: 0;
            border: 1px solid var(--fd-border);
            background: var(--fd-panel);
            color: var(--fd-text);
            font-weight: 950;
            cursor: pointer;
          }
          .fd-tabBtn.active{
            border-color: var(--fd-ok-br);
            background: var(--fd-ok-bg);
          }

          .fd-infoCard{
            border-radius: 22px;
            border: 1px solid var(--fd-border);
            background: var(--fd-card);
            overflow: hidden;
            box-shadow: var(--fd-shadow);
          }

          .fd-infoTop{
            padding: 14px 14px 12px 14px;
            border-bottom: 1px solid var(--fd-border);
            background: radial-gradient(900px 240px at 0% 0%, rgba(34,197,94,0.10), transparent 55%), var(--fd-panel);
          }
          .fd-infoTopRow{ display:flex; gap:10px; flex-wrap:wrap; align-items:center; justify-content:space-between; }
          .fd-infoTitleRow{ display:flex; gap:10px; flex-wrap:wrap; align-items:center; }

          .fd-pill{
            display:inline-flex; align-items:center; gap:8px;
            padding: 7px 10px;
            border-radius: 999px;
            border: 1px solid var(--fd-border);
            background: var(--fd-panel);
            font-weight: 900;
            color: var(--fd-text);
          }
          .fd-pill[data-tone="ok"]{ background: var(--fd-ok-bg); border-color: var(--fd-ok-br); }
          .fd-pill[data-tone="muted"]{ background: var(--fd-soft); }
          .fd-pill[data-tone="ok2"]{ background: rgba(34,197,94,0.10); border-color: rgba(34,197,94,0.28); }
          .fd-pill[data-tone="info"]{ background: var(--fd-info-bg); border-color: var(--fd-info-br); }
          .fd-pill[data-tone="warn"]{ background: var(--fd-warn-bg); border-color: var(--fd-warn-br); }
          .fd-pill[data-tone="done"]{ background: rgba(34,197,94,0.16); border-color: rgba(34,197,94,0.45); }

          .fd-infoBody{ padding: 14px; }

          .fd-noteBox{
            margin-top: 12px;
            border-radius: 18px;
            border: 1px dashed var(--fd-border);
            background: var(--fd-soft);
            padding: 12px 12px;
            line-height: 1.4;
            color: var(--fd-text);
          }

          .fd-kvGrid{ display:grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; margin-top: 12px; }
          @media (max-width: 860px){ .fd-kvGrid{ grid-template-columns: 1fr; } }
          .fd-kv{
            border-radius: 18px;
            border: 1px solid var(--fd-border);
            background: var(--fd-soft);
            padding: 10px 12px;
            display:flex;
            justify-content:space-between;
            gap:12px;
            align-items:center;
          }
          .fd-kvK{ font-size: 12px; font-weight: 950; text-transform: uppercase; letter-spacing: .02em; color: var(--fd-muted); }
          .fd-kvV{ font-weight: 950; color: var(--fd-text); text-align:right; }

          .fd-fg{ display:flex; flex-direction:column; gap:8px; }
          .fd-lbl{ font-size: 12px; font-weight: 900; letter-spacing: .02em; text-transform: uppercase; display:flex; align-items:center; gap:8px; color: var(--fd-muted); }
          .fd-hint{ margin-top: 6px; font-size: 12px; line-height: 1.35; color: var(--fd-muted); }

          .fd-inputXL, .fd-dateXL, .fd-selectXL, .fd-textareaXL{
            width: 100%;
            min-height: 46px;
            padding: 12px 12px;
            border-radius: 16px;
            border: 1px solid var(--fd-border);
            background: var(--surface);
            color: var(--fd-text);
            outline: none;
            transition: border .15s ease, background .15s ease, box-shadow .15s ease;
          }
          .fd-textareaXL{ min-height: 92px; resize: vertical; padding-top: 12px; }
          .fd-inputXL::placeholder{ color: var(--fd-muted-2); }
          .fd-inputXL:focus, .fd-dateXL:focus, .fd-selectXL:focus, .fd-textareaXL:focus{
            border-color: var(--fd-ok-br);
            box-shadow: 0 0 0 3px rgba(34,197,94,0.10);
          }

          .fd-rowGrid{ display:grid; grid-template-columns: 1fr 1fr; gap: 12px; }
          @media (max-width: 860px){ .fd-rowGrid{ grid-template-columns: 1fr; } }

          .fd-catGrid{
            margin-top: 10px;
            display: grid;
            grid-template-columns: repeat(6, minmax(0, 1fr));
            gap: 10px;
          }
          @media (max-width: 980px){ .fd-catGrid{ grid-template-columns: repeat(4, 1fr); } }
          @media (max-width: 680px){ .fd-catGrid{ grid-template-columns: repeat(3, 1fr); } }
          @media (max-width: 420px){ .fd-catGrid{ grid-template-columns: repeat(2, 1fr); } }

          .fd-catTile{
            border-radius: 16px;
            border: 1px solid var(--fd-border);
            background: var(--fd-soft);
            padding: 10px;
            display:flex;
            flex-direction:column;
            gap: 8px;
            cursor: pointer;
            text-align:left;
            transition: transform .12s ease, border .12s ease, background .12s ease;
            color: var(--fd-text);
          }
          .fd-catTile:hover{ transform: translateY(-1px); border-color: var(--fd-border-strong); background: var(--fd-panel); }
          .fd-catTile.isActive{ border-color: var(--fd-ok-br); background: var(--fd-ok-bg); }

          .fd-catImg{
            height: 52px; width: 100%;
            border-radius: 14px;
            overflow:hidden;
            border: 1px solid var(--fd-border);
            background: var(--fd-panel);
            display:flex; align-items:center; justify-content:center;
            position: relative;
          }
          .fd-catImg img{ width: 100%; height: 100%; object-fit: cover; display:block; }
          .fd-catText{ font-weight: 980; font-size: 13px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

          .fd-cropTiles{
            margin-top: 10px;
            display:grid;
            grid-template-columns: repeat(3, minmax(0,1fr));
            gap: 10px;
          }
          @media (max-width: 860px){ .fd-cropTiles{ grid-template-columns: repeat(2, 1fr); } }
          @media (max-width: 520px){ .fd-cropTiles{ grid-template-columns: 1fr; } }

          .fd-cropTile2{
            border-radius: 16px;
            border: 1px solid var(--fd-border);
            background: var(--fd-soft);
            padding: 10px;
            display:flex;
            align-items:center;
            gap: 10px;
            cursor:pointer;
            text-align:left;
            transition: transform .12s ease, border .12s ease, background .12s ease;
            color: var(--fd-text);
          }
          .fd-cropTile2:hover{ transform: translateY(-1px); border-color: var(--fd-border-strong); background: var(--fd-panel); }
          .fd-cropTile2.isActive{ border-color: var(--fd-ok-br); background: var(--fd-ok-bg); }

          .fd-cropTileImg, .fd-cropThumb, .fd-seasonCropThumb{
            height: 44px; width: 44px;
            border-radius: 14px;
            overflow:hidden;
            border: 1px solid var(--fd-border);
            background: var(--fd-panel);
            display:flex; align-items:center; justify-content:center;
            position: relative;
            flex: 0 0 auto;
          }
          .fd-seasonCropThumb{ height: 62px; width: 62px; border-radius: 18px; }
          .fd-cropTileImg img, .fd-cropThumb img, .fd-seasonCropThumb img{ width:100%; height:100%; object-fit: cover; display:block; }
          .fd-cropImgFallback{ display:flex; align-items:center; justify-content:center; width:100%; height:100%; font-size:20px; }

          .fd-cropTileText{ display:flex; flex-direction:column; gap:2px; min-width:0; flex: 1 1 auto; }
          .fd-cropTileName{ font-weight: 980; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
          .fd-cropTileSub{ font-size: 12px; color: var(--fd-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

          .fd-cropSelected{
            margin-top: 10px;
            border-radius: 18px;
            border: 1px solid var(--fd-border);
            background: var(--fd-soft);
            padding: 10px;
            display:flex;
            gap: 10px;
            align-items:center;
            justify-content:space-between;
            flex-wrap: wrap;
          }
          .fd-cropSelected[data-has="0"]{ justify-content:flex-start; }
          .fd-cropSelectedText{ flex: 1 1 auto; min-width: 0; }
          .fd-cropSelectedName{
            font-weight: 980;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .fd-cropSelectedSub{ margin-top: 2px; font-size: 12px; color: var(--fd-muted); }

          .fd-seasonHero{
            margin-top: 10px;
            display:flex;
            gap: 12px;
            align-items:flex-start;
            padding: 10px;
            border-radius: 18px;
            border: 1px solid var(--fd-border);
            background: var(--fd-soft);
            flex-wrap: wrap;
          }
          .fd-seasonCropFallback{
            position:absolute; inset:0;
            display:flex; align-items:center; justify-content:center;
            font-weight: 980; letter-spacing: .02em;
            background: var(--fd-panel);
            color: var(--fd-text);
            opacity: .88;
          }
          .fd-seasonHeroText{ min-width:0; flex: 1 1 auto; }
          .fd-seasonHeroTop{ display:flex; gap:10px; align-items:center; flex-wrap:wrap; }
          .fd-seasonHeroName{
            font-weight: 980;
            font-size: 16px;
            line-height: 1.15;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 100%;
            color: var(--fd-text);
          }
          .fd-seasonHeroSub{
            margin-top: 3px;
            font-size: 12px;
            color: var(--fd-muted);
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .fd-seasonKpiBlock{ margin-top: 12px; display:flex; flex-direction: column; gap: 12px; }
          .fd-seasonKpi{
            border-radius: 16px;
            border: 1px solid var(--fd-border);
            background: var(--fd-soft);
            padding: 12px 14px;
          }
          .fd-seasonKpiK{ font-size: 12px; font-weight: 950; color: var(--fd-muted); text-transform: uppercase; letter-spacing: .03em; }
          .fd-seasonKpiV{ margin-top: 6px; font-size: 15px; font-weight: 980; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; color: var(--fd-text); }

          .fd-seasonYieldWide{
            border-radius: 18px;
            border: 1px solid var(--fd-ok-br);
            background: var(--fd-ok-bg);
            padding: 16px;
          }
          .fd-seasonYieldValue{ margin-top: 6px; font-size: 26px; font-weight: 1000; letter-spacing: .03em; color: var(--fd-text); }

          .fd-seasonCreatedWide{
            border-radius: 16px;
            border: 1px solid var(--fd-border);
            background: var(--fd-panel);
            padding: 14px;
          }
          .fd-seasonCreatedValue{
            margin-top: 6px;
            font-weight: 950;
            font-size: 14px;
            color: var(--fd-text);
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .fd-seasonRange{
            margin-top: 10px;
            border-radius: 18px;
            border: 1px solid var(--fd-border);
            background: var(--fd-soft);
            padding: 10px;
          }
          .fd-seasonRangeTop{
            display:flex;
            gap: 10px;
            align-items:flex-start;
            justify-content:space-between;
            flex-wrap:wrap;
          }
          .fd-seasonRangeLabel{ font-weight: 950; color: var(--fd-text); min-width: 220px; }
          .fd-seasonRangeK{ color: var(--fd-muted); font-weight: 950; text-transform: uppercase; font-size: 12px; letter-spacing: .02em; }
          .fd-seasonRangeV{ font-weight: 980; }
          .fd-seasonRangeMini{ display:flex; gap:8px; flex-wrap:wrap; justify-content:flex-end; }
          .fd-seasonRangeMiniPill{
            padding: 6px 10px;
            border-radius: 999px;
            border: 1px solid var(--fd-border);
            background: var(--fd-panel);
            font-size: 12px;
            font-weight: 900;
            color: var(--fd-text);
            white-space: nowrap;
          }
          .fd-seasonRangeBar{
            margin-top: 10px;
            position: relative;
            height: 28px;
            border-radius: 999px;
            overflow: hidden;
            border: 1px solid var(--fd-border);
            background: var(--fd-panel);
          }
          .fd-seasonRangeBarBg{
            position:absolute; inset:0;
            background: linear-gradient(90deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.03) 50%, rgba(255,255,255,0.06) 100%);
            opacity: .9;
          }
          .fd-seasonRangeBarSeg{
            position:absolute;
            top: 4px; bottom: 4px;
            border-radius: 999px;
            background: var(--fd-ok-bg);
            border: 1px solid var(--fd-ok-br);
          }
          .fd-seasonRangeBarTicks{
            position:absolute; inset:0;
            display:flex;
            justify-content:space-between;
            padding: 0 12px;
            pointer-events:none;
            opacity:.35;
          }
          .fd-seasonRangeBarTicks .t{
            width: 1px;
            background: var(--fd-text);
            margin: 6px 0;
          }
          .fd-seasonRangeBarLegend{
            position:absolute;
            left: 12px; right: 12px; bottom: -18px;
            display:flex; justify-content:space-between;
            font-size: 11px;
            color: var(--fd-muted);
            pointer-events:none;
          }

          .fd-seasonToolbar{ display:flex; align-items:center; justify-content:space-between; gap: 10px; flex-wrap: wrap; margin-bottom: 10px; }
          .fd-seasonNavBtns{ display:flex; gap:10px; align-items:center; }
          .fd-navBtn{
            height: 38px; min-width: 38px;
            padding: 0 12px;
            border-radius: 14px;
            border: 1px solid var(--fd-border);
            background: var(--fd-panel);
            color: var(--fd-text);
            cursor: pointer;
            transition: transform .12s ease, border .12s ease, background .12s ease;
            font-weight: 950;
          }
          .fd-navBtn:hover{ transform: translateY(-1px); border-color: var(--fd-border-strong); }
          .fd-navBtn:disabled{ opacity: .55; cursor: not-allowed; transform:none; }

          .fd-seasonCarousel{
            display:flex; gap: 12px; overflow-x: auto;
            padding: 2px 2px 10px 2px;
            scroll-snap-type: x mandatory;
            -webkit-overflow-scrolling: touch;
          }
          .fd-seasonCarousel::-webkit-scrollbar{ height: 10px; }
          .fd-seasonCarousel::-webkit-scrollbar-thumb{ background: var(--fd-border); border-radius: 999px; }

          .fd-seasonCard{
            scroll-snap-align: start;
            flex: 0 0 440px;
            max-width: 440px;
            min-height: 520px;
            display: flex;
            flex-direction: column;
            border-radius: 20px;
            border: 1px solid var(--fd-border);
            background: var(--fd-card);
            padding: 12px;
            transition: transform .15s ease, border .15s ease;
            color: var(--fd-text);
            box-shadow: var(--fd-shadow);
          }
          @media (max-width: 520px){
            .fd-seasonCard{ flex-basis: 88vw; max-width: 88vw; min-height: 580px; }
          }
          .fd-seasonCard:hover{ transform: translateY(-1px); border-color: var(--fd-border-strong); }
          .fd-seasonCardActive{
            border-color: var(--fd-ok-br) !important;
            background: linear-gradient(180deg, var(--fd-ok-bg), var(--fd-card)) !important;
          }

          .fd-cardTop{ display:flex; justify-content: space-between; align-items:center; gap:10px; flex-wrap: wrap; }
          .fd-cardTitle{ font-weight: 980; font-size: 16px; display:flex; align-items:center; gap:10px; color: var(--fd-text); }
          .fd-statusRow{ display:flex; gap:10px; align-items:center; flex-wrap: wrap; width: 100%; }

          .fd-badge{
            display:inline-flex;
            padding: 6px 10px;
            border-radius: 999px;
            border: 1px solid var(--fd-ok-br);
            background: var(--fd-ok-bg);
            font-weight: 950;
            font-size: 12px;
            letter-spacing: .02em;
            color: var(--fd-text);
          }

          .fd-chip{
            display:inline-flex; align-items:center; gap:8px;
            padding: 8px 10px; border-radius: 999px;
            border: 1px solid var(--fd-border);
            background: var(--fd-panel);
            font-weight: 900;
            color: var(--fd-text);
            max-width: 100%;
            white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
          }

          .fd-iconBtn2{
            display:inline-flex; align-items:center; justify-content:center;
            height: 44px; width: 44px;
            border-radius: 14px;
            border: 1px solid var(--fd-border);
            background: var(--fd-panel);
            color: var(--fd-text);
            cursor: pointer;
            transition: transform .12s ease, border .12s ease, background .12s ease;
            flex: 0 0 auto;
          }
          .fd-iconBtn2:hover{ transform: translateY(-1px); border-color: var(--fd-border-strong); }
          .fd-iconBtn2:disabled{ opacity: .55; cursor: not-allowed; transform:none; }

          .fd-actionsRow{
            margin-top: auto;
            display:flex;
            gap:10px;
            flex-wrap: wrap;
            align-items: stretch;
            padding-top: 12px;
          }
          .fd-actionsRow .btn{
            height: 44px;
            display:flex;
            align-items:center;
            justify-content:center;
          }

          .fd-activeGrid{
            margin-top: 12px;
            display:grid;
            grid-template-columns: 1.1fr 0.9fr;
            gap: 12px;
            align-items: start;
          }
          @media (max-width: 980px){ .fd-activeGrid{ grid-template-columns: 1fr; } }

          .fd-activeLeft,
          .fd-activeRight{
            border-radius: 18px;
            border: 1px solid var(--fd-border);
            background: var(--fd-soft);
            padding: 12px;
          }
          .fd-bigNumber{ font-size: 28px; font-weight: 980; margin-top: 6px; color: var(--fd-text); }
          .fd-miniMuted{ color: var(--fd-muted); font-size: 12px; margin-top: 6px; }

          .fd-statusDD{ display: inline-flex; width: 100%; max-width: 560px; min-width: 220px; isolation: isolate; }
          @media (max-width: 520px){ .fd-statusDD{ max-width: 100%; min-width: 180px; } }
          .fd-statusDD[aria-disabled="true"]{ opacity: .65; }
          .fd-statusDDBtn{
            width: 100%;
            display:flex;
            align-items:center;
            justify-content:space-between;
            gap: 10px;
            border-radius: 999px;
            border: 1px solid var(--fd-border);
            background: var(--fd-panel);
            padding: 12px 16px;
            font-weight: 980;
            font-size: 16px;
            color: var(--fd-text);
            cursor: pointer;
            transition: transform .12s ease, border .12s ease, background .12s ease;
            height: 44px;
          }
          .fd-statusDDBtn:hover{ transform: translateY(-1px); border-color: var(--fd-border-strong); }
          .fd-statusDDBtn:disabled{ cursor:not-allowed; transform:none; }
          .fd-statusDDLabel{ white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
          .fd-statusDDChevron{ color: var(--fd-muted); font-size: 14px; }

          .fd-statusDD[data-tone="muted"] .fd-statusDDBtn{ background: var(--fd-soft); }
          .fd-statusDD[data-tone="ok"] .fd-statusDDBtn{ background: var(--fd-ok-bg); border-color: var(--fd-ok-br); }
          .fd-statusDD[data-tone="ok2"] .fd-statusDDBtn{ background: rgba(34,197,94,0.10); border-color: rgba(34,197,94,0.28); }
          .fd-statusDD[data-tone="info"] .fd-statusDDBtn{ background: var(--fd-info-bg); border-color: var(--fd-info-br); }
          .fd-statusDD[data-tone="warn"] .fd-statusDDBtn{ background: var(--fd-warn-bg); border-color: var(--fd-warn-br); }
          .fd-statusDD[data-tone="done"] .fd-statusDDBtn{ background: rgba(34,197,94,0.16); border-color: rgba(34,197,94,0.45); }

          .fd-statusDDMenu{
            z-index: 9999999;
            border-radius: 20px;
            border: 1px solid rgba(255,255,255,0.10);
            background: rgba(18,24,18,0.97);
            backdrop-filter: blur(20px);
            box-shadow: 0 16px 48px rgba(0,0,0,0.55), 0 2px 8px rgba(0,0,0,0.3);
            padding: 8px;
            overflow: auto;
            min-width: 240px;
          }
          .fd-statusDDItem{
            width: 100%;
            display:flex;
            align-items:center;
            justify-content:space-between;
            gap: 10px;
            border: 0;
            border-radius: 12px;
            padding: 10px 12px;
            background: transparent;
            color: var(--fd-text);
            cursor: pointer;
            text-align:left;
            transition: background .10s ease;
          }
          .fd-statusDDItemLeft{
            display: flex;
            align-items: center;
            gap: 10px;
          }
          .fd-statusDDDot{
            width: 8px;
            height: 8px;
            border-radius: 50%;
            flex-shrink: 0;
          }
          .fd-statusDDIcon{
            font-size: 18px;
            line-height: 1;
            width: 22px;
            text-align: center;
          }
          .fd-statusDDItem:hover,
          .fd-statusDDItem.isHover{ background: rgba(255,255,255,0.07); }
          .fd-statusDDItem.isActive{
            background: rgba(34,197,94,0.12);
            outline: 1px solid rgba(34,197,94,0.28);
          }
          .fd-statusDDItemText{ font-size: 15px; font-weight: 800; line-height: 1.2; }
          .fd-statusDDTick{ font-size: 15px; font-weight: 980; opacity: .95; color: #4ade80; }
          .fd-statusDDTick.ghost{ opacity: 0; }

          .fd-modal{
            position: fixed; inset: 0;
            background: rgba(0,0,0,0.45);
            backdrop-filter: blur(8px);
            display:flex; align-items:center; justify-content:center;
            z-index: 4000;
            padding: 18px;
          }
          .fd-modalCard{
            width: min(860px, 96vw);
            border-radius: 22px;
            border: 1px solid var(--fd-border);
            background: var(--modal-bg);
            box-shadow: var(--fd-shadow);
            overflow: hidden;
            max-height: 90vh;
            display: flex;
            flex-direction: column;
          }
          .fd-modalCard.fd-modalCardWide{
            width: min(1180px, 98vw);
          }
          .fd-modalHead{
            padding: 14px 14px 12px 14px;
            display:flex; justify-content:space-between; align-items:center; gap:12px;
            border-bottom: 1px solid var(--fd-border);
            background: linear-gradient(180deg, rgba(34,197,94,0.10), transparent);
          }
          .fd-modalTitle{ font-weight: 980; font-size: 18px; color: var(--fd-text); }
          .fd-modalSub{ margin-top: 2px; color: var(--fd-muted); font-weight: 700; font-size: 13px; }
          .fd-modalBody{ padding: 14px; color: var(--fd-text); overflow-y: auto; }
          .fd-stickyActions{
            position: sticky;
            bottom: 0;
            margin-top: 14px;
            padding-top: 12px;
            display:flex;
            gap: 10px;
            justify-content:flex-end;
            background: linear-gradient(180deg, transparent, var(--modal-bg));
            flex-wrap: wrap;
          }

          .fd-dangerCard{
            border-radius: 18px;
            border: 1px solid var(--fd-danger-br);
            background: var(--fd-danger-bg);
            padding: 14px;
            color: var(--fd-text);
          }

          .fd-topActionRow{
            display:flex;
            gap:10px;
            flex-wrap:wrap;
            align-items:center;
          }

          .fd-fieldActionBtnDanger{
            border: 1px solid var(--fd-danger-br) !important;
            background: var(--fd-danger-bg) !important;
            color: var(--fd-text) !important;
          }

          .fd-mapWrap{
            border-radius: 18px;
            overflow: hidden;
            border: 1px solid var(--fd-border);
            background: var(--fd-soft);
          }

          .fd-mapEditor{
            display:flex;
            flex-direction:column;
            gap:12px;
          }
          .fd-mapEditorToolbar{
            display:flex;
            gap:10px;
            flex-wrap:wrap;
          }
          .fd-mapEditorCanvas{
            width:100%;
            min-height: 420px;
            border-radius: 18px;
            overflow: hidden;
            border: 1px solid var(--fd-border);
            background: var(--surface-soft);
          }
          .fd-mapEditorHint{
            font-size:12px;
            color: var(--fd-muted);
            line-height:1.4;
          }
          .fd-mapEditorError{
            padding: 14px;
            border-radius: 16px;
            border: 1px solid var(--fd-danger-br);
            background: var(--fd-danger-bg);
            color: var(--fd-text);
            font-weight: 800;
          }

          .fd-contourLayout{
            display:grid;
            grid-template-columns: minmax(0, 1.2fr) minmax(300px, 0.8fr);
            gap:14px;
            align-items:start;
          }
          @media (max-width: 980px){
            .fd-contourLayout{
              grid-template-columns: 1fr;
            }
          }

          .fd-sideCard{
            border-radius: 18px;
            border: 1px solid var(--fd-border);
            background: var(--fd-soft);
            padding: 14px;
          }

          .fd-empty{
            margin-top: 12px;
            opacity: 0.8;
            color: var(--fd-muted);
            font-weight: 850;
          }

          /* Mobile optimizations */
          @media (max-width: 640px) {
            /* Stack section title THEN buttons — each button full-width */
            .fd-sectionHead {
              flex-direction: column;
              align-items: stretch;
              gap: 10px;
            }
            .fd-sectionRight {
              width: 100%;
              max-width: 100%;
              box-sizing: border-box;
            }
            .fd-topActionRow {
              display: flex;
              flex-direction: column;
              width: 100%;
              max-width: 100%;
              gap: 8px;
              box-sizing: border-box;
            }
            .fd-topActionRow .btn {
              width: 100% !important;
              min-height: 48px;
              height: auto;
              padding: 12px 16px;
              font-size: 15px;
              text-align: left;
            }
            .fd-seasonHero {
              flex-direction: column;
            }
            .fd-seasonHero .fd-seasonCropThumb {
              align-self: center;
            }
            .fd-cropSelected {
              flex-direction: column;
              align-items: flex-start;
            }
            .fd-cropSelected .btn {
              width: 100%;
            }
            .fd-stickyActions {
              flex-direction: column-reverse;
            }
            .fd-stickyActions .btn {
              width: 100%;
            }
            .fd-statusRow {
              flex-direction: column;
            }
            .fd-statusRow .fd-statusDD {
              max-width: 100%;
              min-width: 0;
              width: 100%;
            }
            .fd-statusRow .fd-iconBtn2 {
              width: 100%;
            }
            .fd-seasonRangeTop {
              flex-direction: column;
            }
            .fd-seasonRangeMini {
              width: 100%;
              justify-content: flex-start;
            }
            .fd-seasonRangeLabel {
              min-width: auto;
            }
            .fd-cardTop {
              flex-direction: column;
              align-items: stretch;
              gap: 10px;
            }
            .fd-cardTop .fd-statusRow {
              width: 100%;
            }
            .fd-cardTitle {
              width: 100%;
            }
          }

          /* ---- Bottom-sheet modals on mobile ---- */
          @media (max-width: 760px) {
            .fd-modal {
              padding: 0;
              align-items: flex-end;
            }
            .fd-modalCard {
              width: 100vw !important;
              max-height: 96vh;
              border-radius: 22px 22px 0 0 !important;
              border-left: none;
              border-right: none;
              border-bottom: none;
            }
            .fd-modalCard.fd-modalCardWide {
              width: 100vw !important;
            }

            /* Tabs bigger touch target */
            .fd-tabBtn {
              height: 46px;
              font-size: 14px;
            }

            /* Season carousel: enough room for last card + peek of next */
            .fd-seasonCarousel {
              padding-right: 20px;
            }

            /* Season card: must fit inside panel (viewport - content padding - panel padding = vw - 60px) */
            /* Use !important to override the inline @media (max-width: 520px) rule above */
            .fd-seasonCard {
              flex-basis: calc(100vw - 96px) !important;
              max-width: calc(100vw - 96px) !important;
              min-height: auto !important;
            }
          }
        `}</style>

        {msg && (
          <div
            className="fd-toastBox"
            style={{
              borderColor:
                msg.type === "ok" ? "var(--fd-ok-br)" : "var(--fd-danger-br)",
            }}
          >
            <span style={{ fontWeight: 900 }}>{msg.text}</span>
          </div>
        )}

        <div className="fd-tabs">
          <button
            className={`fd-tabBtn ${tab === "overview" ? "active" : ""}`}
            type="button"
            onClick={() => setTab("overview")}
          >
            Огляд
          </button>
          <button
            className={`fd-tabBtn ${tab === "events" ? "active" : ""}`}
            type="button"
            onClick={() => setTab("events")}
          >
            Події
          </button>
          <button
            className={`fd-tabBtn ${tab === "history" ? "active" : ""}`}
            type="button"
            onClick={() => setTab("history")}
          >
            Історія
          </button>
          <button
            className={`fd-tabBtn ${tab === "zones" ? "active" : ""}`}
            type="button"
            onClick={() => setTab("zones")}
          >
            Зони
          </button>
        </div>

        {tab === "overview" ? (
          <>
            <div className="panel" style={{ marginTop: 14 }}>
              <SectionTitle
                title="Інформація про поле"
                right={
                  <div className="fd-topActionRow">
                    <button
                      className="btn"
                      type="button"
                      onClick={() => loadField().catch(() => {})}
                    >
                      ⟳ Оновити поле
                    </button>

                    <button
                      className="btn"
                      type="button"
                      onClick={openFieldEdit}
                      disabled={!field}
                    >
                      ✎ Редагувати поле
                    </button>

                    <button
                      className="btn primary"
                      type="button"
                      onClick={openContourEdit}
                      disabled={!field}
                    >
                      ⛶ Редагувати контур
                    </button>

                    <button
                      className="btn fd-fieldActionBtnDanger"
                      type="button"
                      onClick={() => setDeleteOpen(true)}
                      disabled={!field}
                    >
                      🗑 Видалити поле
                    </button>
                  </div>
                }
              />

              {loading ? (
                <div className="fd-empty">Завантажую...</div>
              ) : !field ? (
                <div className="fd-empty">Нема даних поля</div>
              ) : (
                <div className="fd-infoCard" style={{ marginTop: 12 }}>
                  <div className="fd-infoTop">
                    <div className="fd-infoTopRow">
                      <div className="fd-infoTitleRow">
                        <span className="fd-pill" data-tone="ok">
                          {fieldStats.haText}
                        </span>
                        <span className="fd-pill" data-tone={fieldTone}>
                          {fieldStats.contourText}
                        </span>
                        <span className="fd-pill" data-tone="muted">
                          Група: {fieldStats.groupText}
                        </span>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          gap: 10,
                          flexWrap: "wrap",
                          alignItems: "center",
                        }}
                      >
                        <span className="fd-pill" data-tone="muted">
                          ID: #{field?.id ?? id}
                        </span>
                      </div>
                    </div>

                    <div className="fd-kvGrid">
                      <KV k="Назва" v={emptyDash(field?.name)} />
                      <KV k="Площа" v={fieldStats.haText} />
                      <KV k="Контур" v={currentGeometry ? "Так ✅" : "Ні —"} />
                      <KV k="Група" v={fieldStats.groupText} />
                    </div>

                    {field?.note ? (
                      <div className="fd-noteBox">📝 {fieldStats.noteText}</div>
                    ) : null}
                  </div>

                  <div className="fd-infoBody">
                    <div className="fd-mapWrap">
                      <MapboxFieldView
                        height={420}
                        geometry={currentGeometry}
                        bbox={bbox}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="panel" style={{ marginTop: 14 }}>
              <SectionTitle
                title="Сезони поля"
                right={
                  <>
                    <button className="btn" onClick={() => loadSeasons().catch(() => {})} type="button">
                      ⟳ Оновити
                    </button>
                    <button className="btn primary" onClick={openCreateSeason} type="button">
                      + Додати сезон
                    </button>
                  </>
                }
              />

              {seasonsLoading ? (
                <div className="fd-empty">Завантажую сезони...</div>
              ) : (
                <>
                  <div className="fd-infoCard" style={{ marginTop: 12 }}>
                    <div className="fd-infoTop">
                      <div className="fd-infoTopRow">
                        <div className="fd-infoTitleRow">
                          <span className="fd-pill" data-tone="muted">Активний сезон</span>
                          <span className="fd-pill" data-tone="ok">
                            {activeSeason?.year ? `Рік: ${activeSeason.year}` : "—"}
                          </span>
                          {activeSeason?.seasonField ? (
                            <span className="fd-pill" data-tone={activeTone}>
                              {seasonFieldLabel(activeSeason.seasonField)}
                            </span>
                          ) : null}
                        </div>

                        {activeSeason ? (
                          <div className="fd-statusRow" style={{ justifyContent: "flex-end" }}>
                            {(() => {
                              const { kind, key } = pickSeasonKey(activeSeason);
                              const lockKey = `${kind}:${key || activeSeason.year || ""}`;
                              const savingThis = statusSavingKey === lockKey;
                              return (
                                <>
                                  <StatusSelect
                                    value={activeSeason.seasonField || "PLANNED"}
                                    tone={activeTone}
                                    disabled={savingThis}
                                    onChange={(v) => quickChangeStatus(activeSeason, v)}
                                  />
                                  <button
                                    className="fd-iconBtn2"
                                    type="button"
                                    onClick={() => openEditSeason(activeSeason)}
                                    title="Редагувати сезон"
                                    disabled={editing || savingThis}
                                  >
                                    ✎
                                  </button>
                                </>
                              );
                            })()}
                          </div>
                        ) : (
                          <span style={{ color: "var(--fd-muted)" }}>Нема активного сезону</span>
                        )}
                      </div>

                      {activeSummary ? (
                        <>
                          <div className="fd-seasonHero">
                            <div className="fd-seasonCropThumb" title={activeSummary.crop}>
                              {activeSummary.cropImg ? (
                                <img
                                  src={activeSummary.cropImg}
                                  alt={activeSummary.crop}
                                  onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                    if (e.currentTarget.nextSibling) e.currentTarget.nextSibling.style.display = "flex";
                                  }}
                                />
                              ) : null}
                              {activeSummary.cropImg
                                ? <div className="fd-seasonCropFallback" style={{ display: "none" }}>🌿</div>
                                : <div className="fd-seasonCropFallback">🌿</div>}
                            </div>

                            <div className="fd-seasonHeroText">
                              <div className="fd-seasonHeroTop">
                                <div className="fd-seasonHeroName" title={activeSummary.crop}>
                                  {activeSummary.crop}
                                </div>
                                <span className="fd-pill" data-tone={activeTone}>
                                  {activeSummary.status}
                                </span>
                              </div>
                              <div className="fd-seasonHeroSub" title={activeSummary.cropSub || ""}>
                                {activeSummary.cropSub || " "}
                              </div>

                              <div className="fd-seasonKpiBlock">
                                <div className="fd-seasonKpi">
                                  <div className="fd-seasonKpiK">Сорт</div>
                                  <div className="fd-seasonKpiV">{activeSummary.variety}</div>
                                </div>

                                <div className="fd-seasonKpi">
                                  <div className="fd-seasonKpiK">Рік</div>
                                  <div className="fd-seasonKpiV">{activeSummary.year}</div>
                                </div>
                              </div>
                            </div>
                          </div>

                          <SeasonRange
                            year={activeSummary.year}
                            sowingDate={activeSummary.sowRaw}
                            harvestDate={activeSummary.harvRaw}
                          />

                          <div className="fd-activeGrid">
                            <div className="fd-activeLeft">
                              <div style={{ fontWeight: 980, color: "var(--fd-text)" }}>Коротко</div>
                              <div className="fd-kvGrid" style={{ marginTop: 10 }}>
                                <KV k="Статус" v={activeSummary.status} />
                                <KV k="Рік" v={activeSummary.year} />
                                <KV k="Культура" v={activeSummary.crop} />
                                <KV k="Сорт" v={activeSummary.variety} />
                              </div>
                              {activeSummary.note ? (
                                <div className="fd-noteBox" style={{ marginTop: 12 }}>
                                  📝 {activeSummary.note}
                                </div>
                              ) : null}
                            </div>

                            <div className="fd-activeRight">
                              <div
                                style={{
                                  marginTop: 12,
                                  borderRadius: 16,
                                  border: "1px solid var(--fd-border)",
                                  background: "var(--fd-soft)",
                                  padding: 10,
                                }}
                              >
                                <div style={{ fontSize: 12, fontWeight: 950, color: "var(--fd-muted)", textTransform: "uppercase", letterSpacing: ".02em" }}>
                                  Дати
                                </div>
                                <div style={{ marginTop: 8, display: "grid", gap: 8 }}>
                                  <KV k="Посів" v={activeSummary.sow} />
                                  <KV k="Збір" v={activeSummary.harv} />
                                </div>
                              </div>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div style={{ marginTop: 12, color: "var(--fd-muted)" }}>
                          Додай сезон і зроби його активним — тоді тут буде головна картка з усім по полю.
                        </div>
                      )}
                    </div>
                  </div>

                  <div style={{ marginTop: 14 }}>
                    <div className="fd-seasonToolbar">
                      <div style={{ fontWeight: 980, color: "var(--fd-text)" }}>Всі сезони</div>

                      <div className="fd-seasonNavBtns">
                        <div style={{ color: "var(--fd-muted)", fontWeight: 800, fontSize: 13 }}>
                          {seasonsSorted.length ? `Знайдено: ${seasonsSorted.length}` : ""}
                        </div>
                        <button className="fd-navBtn" type="button" onClick={() => scrollSeasons(-520)} disabled={seasonsSorted.length <= 1} title="Вліво">
                          ←
                        </button>
                        <button className="fd-navBtn" type="button" onClick={() => scrollSeasons(520)} disabled={seasonsSorted.length <= 1} title="Вправо">
                          →
                        </button>
                      </div>
                    </div>

                    {seasonsSorted.length === 0 ? (
                      <div style={{ color: "var(--fd-muted)" }}>Сезонів ще немає. Додай перший.</div>
                    ) : (
                      <div className="fd-seasonCarousel" ref={seasonCarouselRef}>
                        {seasonsSorted.map((s) => {
                          const isActive =
                            !!s.active || String(activeSeason?.year) === String(s.year);
                          const tone = statusTone(s?.seasonField);

                          const { kind, key } = pickSeasonKey(s);
                          const lockKey = `${kind}:${key || s.year || ""}`;
                          const savingThis = statusSavingKey === lockKey;

                          const cropObj = resolveSeasonCrop(s, crops);
                          const cropName = cropObj
                            ? cropDisplayName(cropObj)
                            : s.cropName || s?.crop?.name || "—";
                          const cropImg = cropObj
                            ? cropImgSrc(cropObj)
                            : seasonCropId(s)
                            ? `/crops/crop-${seasonCropId(s)}.png`
                            : "";
                          const cropSub = cropObj ? cropSubline(cropObj) : "";

                          return (
                            <div
                              key={String(s.id ?? s.year)}
                              className={`fd-seasonCard ${isActive ? "fd-seasonCardActive" : ""}`}
                            >
                              <div className="fd-cardTop">
                                <div className="fd-cardTitle">
                                  <span>{s.year ? `Сезон ${s.year}` : "Сезон"}</span>
                                  {isActive ? <span className="fd-badge">active</span> : null}
                                </div>

                                <div className="fd-statusRow" style={{ justifyContent: "flex-end" }}>
                                  <StatusSelect
                                    value={s.seasonField || "PLANNED"}
                                    tone={tone}
                                    disabled={savingThis}
                                    onChange={(v) => quickChangeStatus(s, v)}
                                  />
                                  <button
                                    className="fd-iconBtn2"
                                    type="button"
                                    onClick={() => openEditSeason(s)}
                                    title="Редагувати"
                                    disabled={editing || savingThis}
                                  >
                                    ✎
                                  </button>
                                </div>
                              </div>

                              <div className="fd-seasonHero">
                                <div className="fd-seasonCropThumb" title={cropName}>
                                  {cropImg ? (
                                    <img
                                      src={cropImg}
                                      alt={cropName}
                                      onError={(e) => {
                                        e.currentTarget.style.display = "none";
                                        if (e.currentTarget.nextSibling) e.currentTarget.nextSibling.style.display = "flex";
                                      }}
                                    />
                                  ) : null}
                                  {cropImg
                                    ? <div className="fd-seasonCropFallback" style={{ display: "none" }}>🌿</div>
                                    : <div className="fd-seasonCropFallback">🌿</div>}
                                </div>

                                <div className="fd-seasonHeroText">
                                  <div className="fd-seasonHeroTop">
                                    <div className="fd-seasonHeroName" title={cropName}>
                                      {cropName}
                                    </div>
                                    <span className="fd-pill" data-tone={tone}>
                                      {seasonFieldLabel(s.seasonField)}
                                    </span>
                                  </div>
                                  <div className="fd-seasonHeroSub" title={cropSub || ""}>
                                    {cropSub || (s?.cropId ? `id: ${s.cropId}` : " ")}
                                  </div>
                                </div>
                              </div>

                              <div className="fd-seasonKpiBlock">
                                <div className="fd-seasonKpi">
                                  <div className="fd-seasonKpiK">Сорт</div>
                                  <div className="fd-seasonKpiV">{s.varietyName || "—"}</div>
                                </div>

                                <div className="fd-seasonCreatedWide">
                                  <div className="fd-seasonKpiK">Створено</div>
                                  <div className="fd-seasonCreatedValue">
                                    {s.createdAt ? fmtDateTime(s.createdAt) : "—"}
                                  </div>
                                </div>
                              </div>

                              <SeasonRange
                                year={s.year}
                                sowingDate={s.sowingDate}
                                harvestDate={s.harvestDate}
                              />

                              {s.notes ? (
                                <div style={{ marginTop: 10, color: "var(--fd-text)" }}>{s.notes}</div>
                              ) : null}

                              <div className="fd-actionsRow">
                                <button
                                  className={`btn ${isActive ? "" : "primary"}`}
                                  type="button"
                                  disabled={isActive || activatingYear}
                                  onClick={() => activateSeason(s.year)}
                                  style={{ flex: "1 1 100%" }}
                                >
                                  {isActive
                                    ? "Активний ✅"
                                    : activatingYear === s.year
                                    ? "Активую..."
                                    : "Зробити активним"}
                                </button>
                                {savingThis ? <span className="fd-chip">Зберігаю...</span> : null}
                              </div>
                            </div>
                          );
                        })}
                        {/* Spacer so last card scrolls fully into view */}
                        <div style={{ flex: "0 0 4px" }} aria-hidden="true" />
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </>
        ) : tab === "events" ? (
          <FieldEventPanel user={user} fieldId={id} onToast={toast} fieldGeometry={currentGeometry} previousFieldGeometry={previousGeometry} activeSeasonId={activeSeason?.id} onGoToOverview={() => setTab("overview")} />
        ) : tab === "zones" ? (
          <ClimateZonePanel user={user} fieldId={id} fieldGeometry={currentGeometry} onToast={toast} />
        ) : (
          <FieldHistoryPanel
            user={user}
            fieldId={id}
            seasons={seasons}
            onToast={toast}
            reloadKey={historyReloadKey}
          />
        )}

        {fieldEditOpen && (
          <div className="fd-modal" role="dialog" aria-modal="true" onClick={closeFieldEdit}>
            <div className="fd-modalCard" onClick={(e) => e.stopPropagation()}>
              <div className="fd-modalHead">
                <div>
                  <div className="fd-modalTitle">Редагувати поле</div>
                  <div className="fd-modalSub">
                    Змінити назву, площу, нотатку і групу
                  </div>
                </div>
                <button className="btn" onClick={closeFieldEdit} type="button">
                  ✕
                </button>
              </div>

              <form className="fd-modalBody" onSubmit={handleSaveField}>
                <div className="fd-rowGrid">
                  <div className="fd-fg">
                    <div className="fd-lbl">Назва поля</div>
                    <input
                      className="fd-inputXL"
                      value={efName}
                      onChange={(e) => setEfName(e.target.value)}
                      placeholder="Напр: Поле 12"
                      disabled={fieldSaving}
                    />
                  </div>

                  <div className="fd-fg">
                    <div className="fd-lbl">Площа (ha)</div>
                    <input
                      className="fd-inputXL"
                      value={efArea}
                      onChange={(e) => setEfArea(e.target.value)}
                      inputMode="decimal"
                      placeholder="Напр: 15.7"
                      disabled={fieldSaving}
                    />
                  </div>
                </div>

                <div style={{ marginTop: 12 }} className="fd-rowGrid">
                  <div className="fd-fg">
                    <div className="fd-lbl">Група</div>
                    <select
                      className="fd-selectXL"
                      value={efGroupId}
                      onChange={(e) => setEfGroupId(e.target.value)}
                      disabled={fieldSaving}
                    >
                      <option value="">Оберіть групу</option>
                      {groups.map((g) => (
                        <option key={g.id} value={String(g.id)}>
                          {g.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="fd-fg">
                    <div className="fd-lbl">Контур</div>
                    <div className="fd-hint">
                      Для редагування контуру використовуй окрему кнопку
                      <b> "Редагувати контур"</b> у верхній панелі.
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: 12 }} className="fd-fg">
                  <div className="fd-lbl">Нотатка</div>
                  <textarea
                    className="fd-textareaXL"
                    value={efNote}
                    onChange={(e) => setEfNote(e.target.value)}
                    maxLength={1000}
                    disabled={fieldSaving}
                    placeholder="Нотатка до поля"
                  />
                </div>

                <div className="fd-stickyActions">
                  <button className="btn" type="button" onClick={closeFieldEdit} disabled={fieldSaving}>
                    Скасувати
                  </button>
                  <button className="btn primary" type="submit" disabled={fieldSaving}>
                    {fieldSaving ? "Зберігаю..." : "Зберегти зміни"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {contourEditOpen && (
          <div className="fd-modal" role="dialog" aria-modal="true" onClick={closeContourEdit}>
            <div className="fd-modalCard fd-modalCardWide" onClick={(e) => e.stopPropagation()}>
              <div className="fd-modalHead">
                <div>
                  <div className="fd-modalTitle">Редагувати контур поля</div>
                  <div className="fd-modalSub">
                    Можна пересувати точки, уточнювати полігон або перемалювати контур повністю
                  </div>
                </div>
                <button className="btn" onClick={closeContourEdit} type="button">
                  ✕
                </button>
              </div>

              <div className="fd-modalBody">
                <div className="fd-contourLayout">
                  <div>
                    <MapboxFieldEditor
                      geometry={editedGeometry || currentGeometry}
                      height={560}
                      onChange={setEditedGeometry}
                    />
                  </div>

                  <div className="fd-sideCard">
                    <div className="fd-sectionTitle" style={{ fontSize: 16 }}>Поточні дані</div>

                    <div className="fd-kvGrid" style={{ marginTop: 12, gridTemplateColumns: "1fr" }}>
                      <KV k="Назва поля" v={emptyDash(field?.name)} />
                      <KV k="Площа" v={fieldStats.haText} />
                      <KV k="Група" v={currentGroupName} />
                      <KV
                        k="Контур"
                        v={editedGeometry ? "Новий контур готовий ✅" : currentGeometry ? "Є поточний контур" : "Порожньо"}
                      />
                    </div>

                    <div className="fd-noteBox" style={{ marginTop: 12 }}>
                      Порада: якщо треба перемалювати поле точніше, спочатку натисни
                      <b> "Очистити"</b>, потім <b>"Малювати контур"</b>.
                    </div>

                    <div className="fd-stickyActions">
                      <button className="btn" type="button" onClick={closeContourEdit} disabled={contourSaving}>
                        Скасувати
                      </button>
                      <button className="btn primary" type="button" onClick={handleSaveContour} disabled={contourSaving}>
                        {contourSaving ? "Зберігаю контур..." : "Зберегти контур"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {deleteOpen && (
          <div className="fd-modal" role="dialog" aria-modal="true" onClick={() => !deletingField && setDeleteOpen(false)}>
            <div className="fd-modalCard" onClick={(e) => e.stopPropagation()}>
              <div className="fd-modalHead">
                <div>
                  <div className="fd-modalTitle">Видалити поле</div>
                  <div className="fd-modalSub">
                    Цю дію не можна скасувати
                  </div>
                </div>
                <button className="btn" onClick={() => !deletingField && setDeleteOpen(false)} type="button">
                  ✕
                </button>
              </div>

              <div className="fd-modalBody">
                <div className="fd-dangerCard">
                  <div style={{ fontWeight: 900, marginBottom: 8 }}>
                    Ти дійсно хочеш видалити поле <b>{field?.name || `#${id}`}</b>?
                  </div>
                  <div style={{ color: "var(--fd-muted)" }}>
                    Після підтвердження поле буде видалене, і ти повернешся на список полів.
                  </div>
                </div>

                <div className="fd-stickyActions">
                  <button
                    className="btn"
                    type="button"
                    onClick={() => setDeleteOpen(false)}
                    disabled={deletingField}
                  >
                    Скасувати
                  </button>
                  <button
                    className="btn"
                    type="button"
                    onClick={handleDeleteField}
                    disabled={deletingField}
                    style={{
                      borderColor: "var(--fd-danger-br)",
                      background: "var(--fd-danger-bg)",
                      color: "var(--fd-text)",
                    }}
                  >
                    {deletingField ? "Видаляю..." : "Так, видалити"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {createOpen && (
          <div className="fd-modal" role="dialog" aria-modal="true" onClick={closeCreateSeason}>
            <div className="fd-modalCard" onClick={(e) => e.stopPropagation()}>
              <div className="fd-modalHead">
                <div>
                  <div className="fd-modalTitle">Новий сезон</div>
                  <div className="fd-modalSub"></div>
                </div>
                <button className="btn" onClick={closeCreateSeason} type="button" aria-label="Закрити">
                  ✕
                </button>
              </div>

              <form className="fd-modalBody" onSubmit={handleCreateSeason}>
                <div className="fd-rowGrid">
                  <div className="fd-fg">
                    <div className="fd-lbl">Рік</div>
                    <select className="fd-selectXL" value={sYear} onChange={(e) => setSYear(e.target.value)} disabled={creating}>
                      {yearOptions.map((y) => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>

                  <div className="fd-fg">
                    <div className="fd-lbl">Назва</div>
                    <input
                      className="fd-inputXL"
                      value={sSeasonName}
                      onChange={(e) => setSSeasonName(e.target.value)}
                      maxLength={100}
                      placeholder="Напр: Весняний 2025"
                      disabled={creating}
                    />
                  </div>
                </div>

                <div style={{ marginTop: 12 }}>
                  <CropPicker
                    crops={crops}
                    loading={cropsLoading}
                    category={sCropCategory}
                    onCategory={setSCropCategory}
                    cropId={sCropId}
                    onCropId={setSCropId}
                    disabled={creating}
                  />
                </div>

                <div style={{ marginTop: 12 }} className="fd-rowGrid">
                  <div className="fd-fg">
                    <div className="fd-lbl">Сорт</div>
                    <input
                      className="fd-inputXL"
                      value={varietyName}
                      onChange={(e) => setVarietyName(e.target.value)}
                      maxLength={80}
                      placeholder="Напр: Дніпровська 18"
                      disabled={creating}
                    />
                  </div>

                  <div className="fd-fg">
                    <div className="fd-lbl">Статус сезону</div>
                    <StatusSelect
                      value={seasonField}
                      tone={statusTone(seasonField)}
                      disabled={creating}
                      onChange={(v) => setSeasonField(v)}
                    />
                  </div>
                </div>

                <div style={{ marginTop: 12 }} className="fd-rowGrid">
                  <div className="fd-fg">
                    <div className="fd-lbl">Дата посіву</div>
                    <input className="fd-dateXL" type="date" value={sowingDate} onChange={(e) => setSowingDate(e.target.value)} disabled={creating} />
                  </div>
                  <div className="fd-fg">
                    <div className="fd-lbl">Дата збору</div>
                    <input className="fd-dateXL" type="date" value={harvestDate} onChange={(e) => setHarvestDate(e.target.value)} disabled={creating} />
                  </div>
                </div>

                <div style={{ marginTop: 12 }} className="fd-fg">
                  <div className="fd-lbl">Нотатки</div>
                  <textarea className="fd-textareaXL" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={700} disabled={creating} />
                </div>

                <div className="fd-stickyActions">
                  <button className="btn" type="button" onClick={closeCreateSeason} disabled={creating}>
                    Скасувати
                  </button>
                  <button className="btn primary" type="submit" disabled={creating}>
                    {creating ? "Створюю..." : "Створити сезон"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {editOpen && (
          <div className="fd-modal" role="dialog" aria-modal="true" onClick={closeEditSeason}>
            <div className="fd-modalCard" onClick={(e) => e.stopPropagation()}>
              <div className="fd-modalHead">
                <div>
                  <div className="fd-modalTitle">Редагувати сезон</div>
                  <div className="fd-modalSub">Зміна культури / дат / статусу / урожайності</div>
                </div>
                <button className="btn" onClick={closeEditSeason} type="button" aria-label="Закрити">
                  ✕
                </button>
              </div>

              <form className="fd-modalBody" onSubmit={handleSaveEditSeason}>
                <div className="fd-rowGrid">
                  <div className="fd-fg">
                    <div className="fd-lbl">Рік</div>
                    <select className="fd-selectXL" value={eYear} onChange={(e) => setEYear(e.target.value)} disabled={true}>
                      {yearOptions.map((y) => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                    <div className="fd-hint">Рік змінювати не можна (unique: field + year).</div>
                  </div>

                  <div className="fd-fg">
                    <div className="fd-lbl">Назва</div>
                    <input
                      className="fd-inputXL"
                      value={eSeasonName}
                      onChange={(e) => setESeasonName(e.target.value)}
                      maxLength={100}
                      placeholder="Напр: Весняний 2025"
                      disabled={editing}
                    />
                  </div>
                </div>

                <div style={{ marginTop: 12 }}>
                  <CropPicker
                    crops={crops}
                    loading={cropsLoading}
                    category={eCropCategory}
                    onCategory={setECropCategory}
                    cropId={eCropId}
                    onCropId={setECropId}
                    disabled={editing}
                  />
                </div>

                <div style={{ marginTop: 12 }} className="fd-rowGrid">
                  <div className="fd-fg">
                    <div className="fd-lbl">Сорт</div>
                    <input className="fd-inputXL" value={eVarietyName} onChange={(e) => setEVarietyName(e.target.value)} maxLength={80} disabled={editing} />
                  </div>

                  <div className="fd-fg">
                    <div className="fd-lbl">Статус сезону</div>
                    <StatusSelect value={eSeasonField} tone={statusTone(eSeasonField)} disabled={editing} onChange={(v) => setESeasonField(v)} />
                  </div>
                </div>

                <div style={{ marginTop: 12 }} className="fd-rowGrid">
                  <div className="fd-fg">
                    <div className="fd-lbl">Дата посіву</div>
                    <input className="fd-dateXL" type="date" value={eSowingDate} onChange={(e) => setESowingDate(e.target.value)} disabled={editing} />
                  </div>
                  <div className="fd-fg">
                    <div className="fd-lbl">Дата збору</div>
                    <input className="fd-dateXL" type="date" value={eHarvestDate} onChange={(e) => setEHarvestDate(e.target.value)} disabled={editing} />
                  </div>
                </div>

                <div style={{ marginTop: 12 }} className="fd-fg">
                  <div className="fd-lbl">Нотатки</div>
                  <textarea className="fd-textareaXL" value={eNotes} onChange={(e) => setENotes(e.target.value)} maxLength={700} disabled={editing} />
                </div>

                <div className="fd-stickyActions">
                  <button className="btn" type="button" onClick={closeEditSeason} disabled={editing}>
                    Скасувати
                  </button>
                  <button className="btn primary" type="submit" disabled={editing}>
                    {editing ? "Зберігаю..." : "Зберегти зміни"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}